import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import api, { fmt, fmtInt, fmtNum, fmtQty, fmtDate } from '../../utils/api';
import { confirm } from '../../utils/confirm';
import * as XLSX from 'xlsx';

// Uniform metric cell for deal row — keeps columns aligned and visually consistent
function DealMetric({ label, value, color, sub, highlight }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      padding: highlight ? '4px 8px' : '4px 0',
      borderRadius: 4,
      background: highlight ? '#fee2e2' : 'transparent',
      border: highlight ? '1px solid #fca5a5' : '1px solid transparent',
      minWidth: 0,
      width: '100%',
      textAlign: 'center',
    }}>
      <span style={{ fontSize: 10, textTransform: 'uppercase', color: '#6b7280', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </span>
      <span style={{ fontSize: 14, fontWeight: 700, color, fontFamily: "var(--font-mono, monospace)", whiteSpace: 'nowrap' }}>
        {value}
      </span>
      {sub ? <span style={{ fontSize: 10, color: '#6b7280' }}>{sub}</span> : <span style={{ fontSize: 10, height: 12 }} />}
    </div>
  );
}

export default function PricesPage() {
  const { t, i18n } = useTranslation();
  const he = i18n.language === 'he';
  const { hasRole } = useAuth();
  const canEdit = hasRole('admin', 'accounting');
  const [tab, setTab] = useState('agreed');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingPrice, setEditingPrice] = useState({});
  const [creditEdits, setCreditEdits] = useState({});
  const [compOptions, setCompOptions] = useState([]);
  const [compsByMonth, setCompsByMonth] = useState([]);
  const [allSkus, setAllSkus] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [deals, setDeals] = useState([]);
  const [marketing, setMarketing] = useState(null);
  const [mktQuarter, setMktQuarter] = useState(`Q${Math.ceil((new Date().getMonth() + 1) / 3)} ${new Date().getFullYear()}`);
  const [mktNewDesc, setMktNewDesc] = useState('');
  const [mktNewAmount, setMktNewAmount] = useState('');
  const [mktNewSplit, setMktNewSplit] = useState('50/50');
  const [mdfNewDesc, setMdfNewDesc] = useState('');
  const [mdfNewAmount, setMdfNewAmount] = useState('');
  const [expandedDeals, setExpandedDeals] = useState(new Set());
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [expandedComps, setExpandedComps] = useState(new Set());
  const [selectedComps, setSelectedComps] = useState(new Set());
  const [selectedClaims, setSelectedClaims] = useState(new Set()); // orderNo keys for credit claims
  const [dealFilter, setDealFilter] = useState(null); // null | 'HL' | '22K'
  const [creditView, setCreditView] = useState('support'); // 'support' | 'rma' | 'hl' | '22k'
  const [creditsReceived, setCreditsReceived] = useState([]);
  const [newCredit, setNewCredit] = useState({ number: '', amount: '' });

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      if (tab === 'agreed') {
        const res = await api.get('/prices');
        const quarters = Array.isArray(res.data) ? res.data : (res.data.quarters || []);
        setData({ quarters });
        if (res.data.compOptions) setCompOptions(res.data.compOptions);
        if (res.data.compsByMonth) setCompsByMonth(res.data.compsByMonth);
        if (res.data.allSkus) setAllSkus(res.data.allSkus);
      } else if (tab === 'deals') {
        const res = await api.get('/prices/deals');
        setDeals(res.data || []);
      } else if (tab === 'marketing') {
        const [res, crRes] = await Promise.all([
          api.get(`/prices/marketing-expenses?quarter=${encodeURIComponent(mktQuarter)}`),
          api.get(`/prices/credits-received?type=marketing&quarter=${encodeURIComponent(mktQuarter)}`)
        ]);
        setMarketing(res.data);
        setCreditsReceived(crRes.data || []);
      } else if (tab === 'credit' || tab === 'rma') {
        const type = tab === 'rma' ? 'rma' : 'support';
        const [res, crRes] = await Promise.all([
          api.get('/prices/expected-credits'),
          api.get(`/prices/credits-received?type=${type}`)
        ]);
        const credits = Array.isArray(res.data) ? res.data : (res.data.quarters || res.data.credits || []);
        const dealTotals = res.data.dealTotals || [];
        const rmaCheck = res.data.rmaCheck || [];
        setData({ credits, dealTotals, rmaCheck });
        setCreditsReceived(crRes.data || []);
      }
    } catch (err) { console.error('Fetch error:', err); } finally { setLoading(false); }
  }, [tab, mktQuarter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveAgreedPrice = async (quarter, sku, price) => {
    try {
      await api.put('/prices/agreed', { quarter, sku, agreedPrice: parseFloat(price) });
      setEditingPrice({});
      fetchData(true);
    } catch {}
  };

  const linkRebateToComp = async (quarter, sku, compKey, orderNo) => {
    try {
      await api.put('/prices/rebate-link', { quarter, sku, compKey: compKey || null, orderNo });
      fetchData(true);
    } catch {}
  };

  const saveExpectedCredit = async (quarter, sku, creditPerUnit) => {
    try {
      await api.put('/prices/expected-credit', { quarter, sku, expectedCreditPerUnit: parseFloat(creditPerUnit) });
      setCreditEdits({});
      fetchData(true);
    } catch {}
  };

  const saveQuarterlyCredit = async (quarter, amount) => {
    try {
      await api.put('/prices/quarterly-credit', { quarter, creditReceived: parseFloat(amount) });
      fetchData(true);
    } catch {}
  };

  const addCompAlloc = async (compKey) => {
    try {
      await api.post('/prices/comp-alloc', { compKey, sku: '', amount: 0 });
      fetchData(true);
    } catch {}
  };

  const updateCompAlloc = async (allocId, sku, amount) => {
    try {
      await api.put(`/prices/comp-alloc/${allocId}`, { sku, amount: parseFloat(amount) || 0 });
      fetchData(true);
    } catch {}
  };

  const deleteCompAlloc = async (allocId) => {
    try {
      await api.delete(`/prices/comp-alloc/${allocId}`);
      fetchData(true);
    } catch {}
  };

  const exportCompsExcel = () => {
    const rows = [];
    for (const monthGroup of compsByMonth) {
      for (const comp of monthGroup.comps) {
        // Comp header row
        const baseRow = {
          'Month': monthGroup.month,
          'Order No': comp.orderNo,
          'Doc No': comp.externalDocNo,
          'Date': comp.date ? new Date(comp.date).toLocaleDateString('en-GB') : '',
          'Description': comp.description,
          'Comp Amount': comp.amount,
        };

        // Collect all detail rows: linkedRebates + autoMatched
        const details = [];
        if (comp.linkedRebates?.length) {
          for (const lr of comp.linkedRebates) {
            details.push({ order: lr.orderNo || '', sku: lr.displaySku || lr.sku, desc: lr.description, qty: lr.quantity, rebate: lr.rebateAmount, source: 'Linked' });
          }
        }
        if (comp.autoMatched?.length) {
          for (const am of comp.autoMatched) {
            details.push({ order: am.orderNo || '', sku: am.displaySku || am.sku, desc: am.description, qty: am.quantity, rebate: am.rebateAmount || am.creditPerUnit * am.quantity || 0, source: am.dealName || 'Auto-matched' });
          }
        }

        if (details.length === 0) {
          rows.push({ ...baseRow, 'Source': '', 'Linked Order': '', 'SKU': '', 'Product': '', 'Qty': '', 'Rebate': '', 'Status': 'Open' });
        } else {
          details.forEach((d, i) => {
            rows.push({
              ...(i === 0 ? baseRow : { 'Month': '', 'Order No': '', 'Doc No': '', 'Date': '', 'Description': '', 'Comp Amount': '' }),
              'Source': d.source,
              'Linked Order': d.order,
              'SKU': d.sku,
              'Product': d.desc,
              'Qty': d.qty,
              'Rebate': d.rebate,
              'Status': i === 0 ? (comp.isClosed ? 'Closed' : 'Open') : '',
            });
          });
        }
      }
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    // Set column widths
    ws['!cols'] = [
      { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 12 }, { wch: 30 },
      { wch: 14 }, { wch: 14 }, { wch: 28 }, { wch: 10 }, { wch: 14 },
      { wch: 16 }, { wch: 14 }, { wch: 10 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Compensations');
    XLSX.writeFile(wb, 'compensations_breakdown.xlsx');
  };

  const toggleOrder = (key) => setExpandedOrders(prev => {
    const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;

  return (
    <div>
      <div className="sub-tabs">
        <button className={`sub-tab${tab === 'agreed' ? ' active' : ''}`} onClick={() => setTab('agreed')}>
          {t('prices.rebate')} & {t('prices.agreedPrice')}
        </button>
        <button className={`sub-tab${tab === 'deals' ? ' active' : ''}`} onClick={() => setTab('deals')}>
          {he ? 'דילים' : 'Deals'}
        </button>
        <button className={`sub-tab${tab === 'credit' ? ' active' : ''}`} onClick={() => setTab('credit')}>
          {t('prices.quarterlyCredit')}
        </button>
        <button className={`sub-tab${tab === 'marketing' ? ' active' : ''}`} onClick={() => setTab('marketing')}>
          {t('prices.marketing')}
        </button>
        <button className={`sub-tab${tab === 'rma' ? ' active' : ''}`} onClick={() => setTab('rma')}>
          Quarterly RMA
        </button>
      </div>

      {/* ═══ REBATE — AGREED PRICE BY MONTH ═══ */}
      {tab === 'agreed' && data && (
        <div>
          {/* Title */}
          <div className="flex-between" style={{ marginBottom: 4 }}>
            <h2 style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, letterSpacing: 1 }}>
              {he ? 'ריבייט — מחיר מוסכם לפי חודש' : 'REBATE — AGREED PRICE BY MONTH'}
            </h2>
            <div style={{ display: 'flex', gap: 8 }}>
              {selectedClaims.size > 0 && (
                <button className="btn btn-gold" onClick={() => {
                  const rows = [];
                  for (const q of (data.quarters || [])) {
                    for (const order of (q.orders || [])) {
                      if (!selectedClaims.has(order.orderNo)) continue;
                      for (const line of (order.lines || [])) {
                        if (!line.rebate || line.rebate <= 0 || line.linkedCompKey) continue;
                        rows.push({
                          'Quarter': q.quarter,
                          'Order': order.orderNo,
                          'Doc Type': order.docType,
                          'Date': order.orderDate ? new Date(order.orderDate).toLocaleDateString('en-GB') : '',
                          'SKU': line.sku,
                          'Barcode': line.displaySku !== line.sku ? line.displaySku : '',
                          'Product': line.description,
                          'Qty': line.quantity,
                          'Invoice Price': line.invoicePrice,
                          'Agreed Price': line.agreedPrice,
                          'Credit/Unit': line.quantity > 0 ? Math.round((line.rebate / line.quantity) * 100) / 100 : 0,
                          'Total Credit': Math.round(line.rebate * 100) / 100,
                          'Deal': line.dealName || '',
                        });
                      }
                    }
                  }
                  if (!rows.length) return alert('No open credits in selected orders');
                  const totalCredit = rows.reduce((s, r) => s + r['Total Credit'], 0);
                  rows.push({});
                  rows.push({ 'Quarter': '', 'Order': '', 'Doc Type': '', 'Date': '', 'SKU': '', 'Barcode': '', 'Product': 'TOTAL CREDIT DUE', 'Qty': '', 'Invoice Price': '', 'Agreed Price': '', 'Credit/Unit': '', 'Total Credit': Math.round(totalCredit * 100) / 100, 'Deal': '' });
                  const ws = XLSX.utils.json_to_sheet(rows);
                  ws['!cols'] = [{wch:10},{wch:14},{wch:6},{wch:12},{wch:10},{wch:14},{wch:35},{wch:8},{wch:12},{wch:12},{wch:10},{wch:12},{wch:20}];
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, 'Credits Due');
                  XLSX.writeFile(wb, `credits_due_marshall_${new Date().toISOString().slice(0,10)}.xlsx`);
                }}>📥 Export {selectedClaims.size} Orders — Credits Due</button>
              )}
              {selectedClaims.size > 0 && (
                <button className="btn btn-outline btn-sm" onClick={() => setSelectedClaims(new Set())} style={{ color: '#dc2626' }}>✕ Clear</button>
              )}
              <button className="btn btn-sm btn-outline" onClick={() => {
                const openOrders = new Set();
                for (const q of (data.quarters || [])) {
                  for (const order of (q.orders || [])) {
                    const hasOpen = Object.entries(order.dealRebates || {}).some(([, d]) => {
                      const total = typeof d === 'object' ? d.total : d;
                      const linked = typeof d === 'object' ? d.linked : 0;
                      return total > 0 && linked < total - 0.01;
                    });
                    if (hasOpen) openOrders.add(order.orderNo);
                  }
                }
                setSelectedClaims(openOrders);
              }}>{he ? 'בחר פתוחים' : 'Select All Open'}</button>
            </div>
          </div>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 24 }}>
            {he ? 'הזן מחיר מוסכם למוצר — המערכת מחשבת את הריבייט' : 'Enter Agreed Price per Product — the system calculates the Rebate expected (Invoice Price – Agreed Price) × Qty'}
          </p>

          {/* Selected orders sum by deal */}
          {(() => {
            const selectedOrders = (data.quarters || []).flatMap(q => (q.orders || [])).filter(o => selectedClaims.has(o.orderNo));
            const hlSum = selectedOrders.reduce((s, o) => {
              const d = Object.entries(o.dealRebates || {}).find(([k]) => k.includes('Home'));
              return s + (d ? (typeof d[1] === 'object' ? d[1].total : d[1]) : 0);
            }, 0);
            const k22Sum = selectedOrders.reduce((s, o) => {
              const d = Object.entries(o.dealRebates || {}).find(([k]) => k.includes('22K'));
              return s + (d ? (typeof d[1] === 'object' ? d[1].total : d[1]) : 0);
            }, 0);
            // Sum selected compensations
            const selectedCompTotal = (compsByMonth || []).reduce((s, mg) => {
              return s + (mg.comps || []).filter(c => selectedComps.has(c.compKey)).reduce((s2, c) => s2 + Math.abs(c.amount), 0);
            }, 0);

            const hasSelection = selectedClaims.size > 0 || selectedComps.size > 0;
            // Fix: balance/total must include ALL deals (Willen II, PRICE SUPPORT, …), not just HL+22K.
            // The HL/22K chips above remain for display; the Total/Balance now uses the full sum.
            const rebateTotal = selectedOrders.reduce((s, o) => s + Object.values(o.dealRebates || {}).reduce((t, d) => t + (typeof d === 'object' ? d.total : d), 0), 0);
            const balance = rebateTotal - selectedCompTotal;

            return hasSelection ? (
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', padding: '12px 16px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                {selectedClaims.size > 0 && (
                  <>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{selectedClaims.size} {he ? 'הזמנות' : 'orders'}:</span>
                    {hlSum > 0 && <span style={{ padding: '4px 12px', borderRadius: 8, fontSize: 13, fontWeight: 800, background: '#16a34a', color: '#fff' }}>HL {fmt(hlSum)}</span>}
                    {k22Sum > 0 && <span style={{ padding: '4px 12px', borderRadius: 8, fontSize: 13, fontWeight: 800, background: '#1565c0', color: '#fff' }}>22K {fmt(k22Sum)}</span>}
                  </>
                )}
                {selectedComps.size > 0 && (
                  <>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>−</span>
                    <span style={{ padding: '4px 12px', borderRadius: 8, fontSize: 13, fontWeight: 800, background: '#dc2626', color: '#fff' }}>
                      {selectedComps.size} {he ? 'זיכויים' : 'credits'} {fmt(selectedCompTotal)}
                    </span>
                  </>
                )}
                {selectedClaims.size > 0 && selectedComps.size > 0 && (
                  <>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>=</span>
                    <span style={{ padding: '4px 14px', borderRadius: 8, fontSize: 14, fontWeight: 800,
                      background: balance > 0.01 ? '#f59e0b' : balance < -0.01 ? '#dc2626' : '#16a34a',
                      color: '#fff' }}>
                      {he ? 'יתרה' : 'Balance'}: {fmt(balance)}
                    </span>
                  </>
                )}
                {selectedClaims.size > 0 && selectedComps.size === 0 && (
                  <span style={{ padding: '4px 14px', borderRadius: 8, fontSize: 14, fontWeight: 800, background: '#333', color: '#fff' }}>
                    {he ? 'סה"כ' : 'Total'} {fmt(rebateTotal)}
                  </span>
                )}
              </div>
            ) : null;
          })()}

          {(data.quarters || []).length === 0 ? <div className="empty-state">{t('common.noData')}</div> :
          (data.quarters || []).map(q => (
            <div key={q.quarter} style={{ marginBottom: 32 }}>
              {/* Quarter Header */}
              <div className="flex-between mb-8">
                <h3 style={{ fontFamily: 'var(--font-mono, monospace)' }}>{q.quarter}</h3>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <span className="text-danger fw-bold" style={{ fontSize: 15 }}>
                    {he ? 'סה"כ ריבייט:' : 'Total Rebate:'} {fmt(q.totalRebate)}
                  </span>
                  {q.mdfCredit > 0 && (
                    <span style={{ fontSize: 13, color: '#1565c0', fontWeight: 600 }}>
                      MDF: {fmt(q.mdfCredit)}
                    </span>
                  )}
                  {canEdit && (
                    <button className="btn btn-sm" style={{ background: '#1565c0', color: '#fff', fontSize: 11 }}
                      onClick={() => {
                        const amount = prompt('MDF Credit amount for ' + q.quarter + ':');
                        if (!amount || !parseFloat(amount)) return;
                        const notes = prompt('Description (optional):') || 'MDF Credit ' + q.quarter;
                        api.put('/prices/mdf-credit', { quarter: q.quarter, amount: parseFloat(amount), notes })
                          .then(() => fetchData(true))
                          .catch(err => alert('Failed: ' + (err.response?.data?.error || err.message)));
                      }}>+ Add MDF</button>
                  )}
                </div>
              </div>

              {/* Orders */}
              {(q.orders || []).map(order => {
                const orderKey = `${q.quarter}|${order.orderNo}`;
                const isExpanded = expandedOrders.has(orderKey);
                return (
                  <div key={order.orderNo} className="card mb-8" style={{ padding: 0, overflow: 'hidden' }}>
                    {/* Order Header — always visible */}
                    <div className="flex-between" style={{ padding: '10px 16px', cursor: 'pointer', background: isExpanded ? '#fafafa' : 'white' }}
                      onClick={() => toggleOrder(orderKey)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={selectedClaims.has(order.orderNo)} onClick={e => e.stopPropagation()}
                          onChange={() => setSelectedClaims(prev => { const s = new Set(prev); s.has(order.orderNo) ? s.delete(order.orderNo) : s.add(order.orderNo); return s; })}
                          style={{ marginInlineEnd: 2 }} />
                        <span className={`arrow${isExpanded ? ' open' : ''}`}>&#9654;</span>
                        <span className="fw-bold">{order.orderNo}</span>
                        <span className="text-muted" style={{ fontSize: 12 }}>📦 {fmtDate(order.orderDate)}</span>
                        <span className={`badge badge-${(order.origin || '').toLowerCase()}`}>{order.origin === 'CN' ? '🇨🇳 CN' : '🇪🇺 EU'}</span>
                        <span className={`badge badge-${(order.docType || '').toLowerCase()}`}>{order.docType}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                          {fmtQty((order.lines || []).reduce((s, l) => s + (l.quantity || 0), 0))} units
                        </span>
                        <span className="fw-bold">{fmt(order.totalAmount)}</span>
                        {Object.entries(order.dealRebates || {}).map(([deal, data]) => {
                          const amt = typeof data === 'object' ? data.total : data;
                          const linked = typeof data === 'object' ? data.linked : 0;
                          const manual = typeof data === 'object' ? data.manualClosed : undefined;
                          const isClosed = manual === true || (manual === undefined && amt > 0 && linked >= amt - 0.01);
                          const label = deal.includes('Home') ? 'HL' : deal.includes('22K') ? '22K' : deal;
                          const isHL = deal.includes('Home');
                          return isClosed ? (
                            <span key={deal} style={{ fontSize: 12, padding: '4px 12px', borderRadius: 8, fontWeight: 800,
                              background: isHL ? '#16a34a' : '#1565c0', color: '#fff', letterSpacing: 0.5 }}>
                              ✓ {label} {fmt(amt)}
                            </span>
                          ) : (
                            <span key={deal} style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, fontWeight: 700,
                              background: isHL ? '#fef3c7' : '#dbeafe',
                              color: isHL ? '#92400e' : '#1e40af' }}>
                              {label}: {fmt(amt)}
                            </span>
                          );
                        })}
                        {order.totalRebate > 0 && Object.keys(order.dealRebates || {}).length === 0 && (
                          <span className="text-danger fw-bold" style={{ fontSize: 12 }}>
                            {he ? 'ריבייט:' : 'Rebate:'} {fmt(order.totalRebate)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Expanded: line items table */}
                    {isExpanded && (
                      <div style={{ padding: '0 16px 16px' }}>
                        <div className="table-wrap">
                          <table>
                            <thead>
                              <tr>
                                <th>SKU</th>
                                <th>{t('common.product')}</th>
                                <th className="text-end">{t('prices.invoicePrice')}</th>
                                <th className="text-end">Qty</th>
                                <th className="text-end" style={{ background: '#fff9e6', borderBottom: '2px solid #C8A22C' }}>
                                  Agreed
                                </th>
                                <th className="text-end">{t('prices.rebate')}</th>
                                <th className="text-end">Amount</th>
                                {canEdit && <th style={{ fontSize: 11, textTransform: 'uppercase', color: '#1565c0' }}>{t('prices.linkToCredit')}</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {(order.lines || []).map((line, i) => {
                                const pKey = `${q.quarter}|${order.orderNo}|${line.sku}`;
                                const isEd = editingPrice.key === pKey;
                                return (
                                  <tr key={`${line.sku}-${i}`}>
                                    <td>{line.sku}{line.barcode && line.barcode !== line.sku && <div className="text-muted" style={{ fontSize: 10 }}>{line.barcode}</div>}</td>
                                    <td>{line.description}</td>
                                    <td className="text-end">{line.invoicePrice.toFixed(2)}</td>
                                    <td className="text-end fw-bold">{fmtQty(line.quantity)}</td>
                                    <td className="text-end" style={{ background: '#fff9e6' }}>
                                      {canEdit && isEd ? (
                                        <input className="inline-edit" type="number" step="0.01" autoFocus
                                          defaultValue={line.agreedPrice || ''}
                                          onBlur={e => saveAgreedPrice(q.quarter, line.sku, e.target.value)}
                                          onKeyDown={e => e.key === 'Enter' && saveAgreedPrice(q.quarter, line.sku, e.target.value)}
                                          style={{ width: 70, textAlign: 'center' }} />
                                      ) : (
                                        <span onClick={() => canEdit && setEditingPrice({ key: pKey })}
                                          style={{ cursor: canEdit ? 'pointer' : 'default', fontWeight: line.agreedPrice ? 600 : 400 }}>
                                          {line.agreedPrice ? line.agreedPrice.toFixed(0) : '—'}
                                          {line.dealName && <div style={{ fontSize: 9, color: '#1565c0', fontWeight: 400 }}>{line.dealName}</div>}
                                        </span>
                                      )}
                                    </td>
                                    <td className={`text-end fw-bold ${line.rebate > 0 ? 'text-danger' : ''}`}>
                                      {line.rebate > 0 ? fmt(line.rebate) : '—'}
                                    </td>
                                    <td className="text-end fw-bold">{fmt(line.amount)}</td>
                                    {canEdit && (
                                      <td>
                                        {line.rebate > 0 && compOptions.length > 0 && (
                                          <select className="inline-edit" style={{ width: 250, fontSize: 11 }}
                                            value={line.linkedCompKey || ''}
                                            onChange={e => linkRebateToComp(q.quarter, line.sku, e.target.value, order.orderNo)}>
                                            <option value="">— Link to credit —</option>
                                            {compOptions.map(opt => (
                                              <option key={opt.key} value={opt.key}>{opt.label}</option>
                                            ))}
                                          </select>
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {canEdit && Object.keys(order.dealRebates || {}).length > 0 && (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                            {Object.entries(order.dealRebates).map(([deal, data]) => {
                              const amt = typeof data === 'object' ? data.total : data;
                              const linked = typeof data === 'object' ? data.linked : 0;
                              const manual = typeof data === 'object' ? data.manualClosed : undefined;
                              const isClosed = manual === true || (manual === undefined && amt > 0 && linked >= amt - 0.01);
                              const label = deal.includes('Home') ? 'HL' : deal.includes('22K') ? '22K' : deal;
                              return (
                                <button key={deal} style={{
                                  padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                  border: 'none',
                                  background: isClosed ? '#dc2626' : '#16a34a',
                                  color: '#fff',
                                }} onClick={async () => {
                                  await api.put('/prices/order-deal-closed', { orderNo: order.orderNo, dealName: deal, closed: !isClosed });
                                  fetchData(true);
                                }}>{isClosed ? `✕ Re-open ${label}` : `✓ Close ${label}`}</button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {/* ═══ COMPENSATIONS DIRECT BY MONTH ═══ */}
          {compsByMonth.length > 0 && (
            <div style={{ marginTop: 40 }}>
              <div className="flex-between" style={{ marginBottom: 4 }}>
                <h2 style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, letterSpacing: 1 }}>
                  {he ? 'זיכויים ישירים לפי חודש' : 'COMPENSATIONS DIRECT BY MONTH'}
                </h2>
                <div style={{ display: 'flex', gap: 8 }}>
                  {selectedComps.size > 0 && (
                    <button className="btn btn-gold btn-sm" onClick={() => {
                      const rows = [];
                      for (const mg of compsByMonth) {
                        for (const comp of mg.comps) {
                          if (!selectedComps.has(comp.compKey)) continue;
                          rows.push({ 'Order': comp.orderNo, 'Date': comp.date, 'Description': comp.description, 'Amount': comp.amount, 'Type': 'CREDIT', 'SKU': '', 'Product': '', 'Qty': '', 'Credit/Unit': '' });
                          for (const lr of (comp.linkedRebates || [])) {
                            rows.push({ 'Order': lr.orderNo, 'Date': '', 'Description': lr.description, 'Amount': lr.rebateAmount, 'Type': 'LINKED', 'SKU': lr.sku, 'Product': lr.description, 'Qty': lr.quantity, 'Credit/Unit': lr.quantity > 0 ? lr.rebateAmount / lr.quantity : 0 });
                          }
                          for (const am of (comp.autoMatched || [])) {
                            const rebate = am.rebateAmount || (am.creditPerUnit || 0) * (am.quantity || 0);
                            rows.push({ 'Order': am.orderNo || '', 'Date': '', 'Description': am.description, 'Amount': rebate, 'Type': am.dealName || 'AUTO', 'SKU': am.sku, 'Product': am.description, 'Qty': am.quantity, 'Credit/Unit': am.quantity > 0 ? rebate / am.quantity : 0 });
                          }
                        }
                      }
                      const ws = XLSX.utils.json_to_sheet(rows);
                      ws['!cols'] = [{wch:12},{wch:12},{wch:35},{wch:12},{wch:8},{wch:10},{wch:30},{wch:8},{wch:12}];
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, 'Credits');
                      XLSX.writeFile(wb, 'credits_export.xlsx');
                    }}>{he ? 'ייצוא נבחרים' : 'Export Selected'} ({selectedComps.size})</button>
                  )}
                  <button className="btn btn-outline btn-sm" onClick={exportCompsExcel}>{he ? 'ייצוא הכל' : 'Export All'}</button>
                </div>
              </div>
              <p className="text-muted" style={{ fontSize: 13, marginBottom: 24 }}>
                {he ? 'לחץ על כל זיכוי לפירוט' : 'Click each credit to expand and see product breakdown'}
              </p>

              {compsByMonth.map(monthGroup => (
                <div key={monthGroup.month} style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 4, height: 24, background: '#dc3545', borderRadius: 2 }} />
                    <span className="fw-bold" style={{ fontSize: 15 }}>{monthGroup.month}</span>
                    <span className="text-danger fw-bold">{fmt(monthGroup.total)}</span>
                  </div>

                  {monthGroup.comps.map(comp => {
                    const isCompOpen = expandedComps.has(comp.compKey);
                    const isSelected = selectedComps.has(comp.compKey);
                    return (
                      <div key={comp.compKey} className="order-group" style={{ marginBottom: 8 }}>
                        <div className="order-group-header" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                          onClick={() => setExpandedComps(prev => { const s = new Set(prev); s.has(comp.compKey) ? s.delete(comp.compKey) : s.add(comp.compKey); return s; })}>
                          <input type="checkbox" checked={isSelected} onClick={e => e.stopPropagation()}
                            onChange={() => setSelectedComps(prev => { const s = new Set(prev); s.has(comp.compKey) ? s.delete(comp.compKey) : s.add(comp.compKey); return s; })} />
                          <span className={`arrow${isCompOpen ? ' open' : ''}`}>&#9654;</span>
                          <span className="fw-bold">{comp.orderNo}</span>
                          <span className="text-muted" style={{ fontSize: 12 }}>{fmtDate(comp.date)}</span>
                          <span className="text-muted" style={{ fontSize: 12 }}>{comp.description}</span>
                          {/* Deal tag buttons */}
                          {canEdit ? (
                            <span onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 3 }}>
                              {['HL', '22K'].map(tag => {
                                const active = comp.dealTag === tag;
                                const isHL = tag === 'HL';
                                return (
                                  <button key={tag} style={{
                                    padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                                    border: active ? 'none' : '1px solid #e2e8f0',
                                    background: active ? (isHL ? '#fef3c7' : '#dbeafe') : '#fff',
                                    color: active ? (isHL ? '#92400e' : '#1e40af') : '#94a3b8',
                                  }} onClick={async () => {
                                    const newTag = active ? null : tag;
                                    await api.put(`/prices/comp-deal-tag/${comp.compId}`, { dealTag: newTag });
                                    fetchData(true);
                                  }}>{tag}</button>
                                );
                              })}
                            </span>
                          ) : comp.dealTag && (
                            <span style={{
                              padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                              background: comp.dealTag === 'HL' ? '#fef3c7' : '#dbeafe',
                              color: comp.dealTag === 'HL' ? '#92400e' : '#1e40af',
                            }}>{comp.dealTag}</span>
                          )}
                          {comp.isClosed && (
                            <span style={{ background: '#16a34a', color: '#fff', padding: '4px 14px', borderRadius: 8, fontSize: 13, fontWeight: 800, letterSpacing: 0.5 }}>
                              ✓ CLOSED
                            </span>
                          )}
                          <div style={{ flex: 1 }} />
                          <span className="text-danger fw-bold" style={{ fontSize: 16 }}>{fmt(comp.amount)}</span>
                        </div>

                        {isCompOpen && (
                          <div className="order-group-body" style={{ padding: 16 }}>
                            {canEdit && (
                              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                                <button style={{
                                  padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                  border: 'none',
                                  background: comp.isClosed ? '#dc2626' : '#16a34a',
                                  color: '#fff',
                                }} onClick={async () => {
                                  await api.put(`/prices/comp-closed/${comp.compId}`, { closed: comp.isClosed ? false : true });
                                  fetchData(true);
                                }}>{comp.isClosed ? '✕ Re-open this credit' : '✓ Mark as Closed'}</button>
                              </div>
                            )}
                            {comp.linkedRebates.length > 0 && (
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 700, color: '#6c757d', marginBottom: 8, letterSpacing: 0.5 }}>
                                  {he ? 'פירוט מוצרים' : 'Product Breakdown'}
                                </div>
                                <div className="table-wrap">
                                  <table>
                                    <thead><tr>
                                      <th>SI Order</th><th>{t('common.sku')}</th><th>{t('common.product')}</th>
                                      <th className="text-end">{t('warehouse.qty')}</th><th className="text-end">Credit/Unit</th><th className="text-end">{t('common.total')}</th>
                                    </tr></thead>
                                    <tbody>
                                      {comp.linkedRebates.map((lr, i) => (
                                        <tr key={i}>
                                          <td className="text-gold">{lr.orderNo}</td>
                                          <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{lr.sku}{lr.barcode && lr.barcode !== lr.sku && <div className="text-muted" style={{ fontSize: 10, fontFamily: 'Arial,sans-serif' }}>{lr.barcode}</div>}</td>
                                          <td>{lr.description}</td>
                                          <td className="text-end fw-bold">{fmtQty(lr.quantity)}</td>
                                          <td className="text-end">{fmt(lr.quantity > 0 ? lr.rebateAmount / lr.quantity : 0)}</td>
                                          <td className="text-end fw-bold text-danger">{fmt(lr.rebateAmount)}</td>
                                        </tr>
                                      ))}
                                      <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                                        <td colSpan={3}>{t('common.total')} {he ? 'מקושר' : 'Linked'}</td>
                                        <td className="text-end">{fmtQty(comp.linkedRebates.reduce((s, lr) => s + lr.quantity, 0))}</td>
                                        <td></td>
                                        <td className="text-end text-danger">{fmt(comp.totalLinked)}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
                                  {comp.isClosed ? (
                                    <span style={{ background: '#e8f7ef', color: '#1a7a4a', border: '1px solid #b6e2cc', padding: '4px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700 }}>✓ Closed — {fmt(comp.rebateDiff)} diff</span>
                                  ) : (
                                    <span style={{ background: '#fdf2f1', color: '#dc3545', border: '1px solid #f0c0b8', padding: '4px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700 }}>
                                      Open — {fmt(comp.rebateDiff)} remaining
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            {comp.linkedRebates.length === 0 && comp.autoMatched?.length > 0 && (
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 700, color: '#1565c0', marginBottom: 8, letterSpacing: 0.5 }}>
                                  Auto-matched: {comp.matchedDeal}
                                </div>
                                <div className="table-wrap">
                                  <table>
                                    <thead><tr>
                                      <th>SI Order</th><th>{t('common.sku')}</th><th>{t('common.product')}</th>
                                      <th className="text-end">{t('warehouse.qty')}</th><th className="text-end">Original</th><th className="text-end">Promo</th><th className="text-end">Credit</th>
                                    </tr></thead>
                                    <tbody>
                                      {comp.autoMatched.map((am, i) => (
                                        <tr key={i}>
                                          <td className="text-gold">{am.orderNo}</td>
                                          <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{am.sku}{am.barcode && am.barcode !== am.sku && <div className="text-muted" style={{ fontSize: 10, fontFamily: 'Arial,sans-serif' }}>{am.barcode}</div>}</td>
                                          <td>{am.description}</td>
                                          <td className="text-end fw-bold">{fmtQty(am.quantity)}</td>
                                          <td className="text-end">{fmt(am.originalPrice)}</td>
                                          <td className="text-end">{fmt(am.promoPrice)}</td>
                                          <td className="text-end fw-bold text-danger">{fmt(am.credit)}</td>
                                        </tr>
                                      ))}
                                      <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                                        <td colSpan={3}>{t('common.total')}</td>
                                        <td className="text-end">{fmtQty(comp.autoMatched.reduce((s, am) => s + am.quantity, 0))}</td>
                                        <td colSpan={2}></td>
                                        <td className="text-end text-danger">{fmt(comp.autoTotal)}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                                  {comp.isClosed ? (
                                    <span style={{ background: '#e8f7ef', color: '#1a7a4a', padding: '4px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700 }}>✓ Matched — {comp.matchedDeal}</span>
                                  ) : (
                                    <span style={{ background: '#fff3e0', color: '#e65100', padding: '4px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700 }}>
                                      Diff: {fmt(comp.rebateDiff)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            {comp.linkedRebates.length === 0 && (!comp.autoMatched || comp.autoMatched.length === 0) && (
                              <div className="text-muted" style={{ padding: 12 }}>No products matched</div>
                            )}

                            {/* Manual Allocations */}
                            {canEdit && (comp.allocations || []).map(alloc => (
                              <div key={alloc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <select className="inline-edit" style={{ flex: 1, fontSize: 12 }}
                                  value={alloc.sku || ''} onChange={e => updateCompAlloc(alloc.id, e.target.value, alloc.amount)}>
                                  <option value="">{t('prices.addProduct')}...</option>
                                  {allSkus.map(s => (<option key={s.sku} value={s.sku}>{s.displaySku} — {(s.description || '').substring(0, 30)}</option>))}
                                </select>
                                <input className="inline-edit" type="number" step="0.01" defaultValue={alloc.amount || ''}
                                  onBlur={e => updateCompAlloc(alloc.id, alloc.sku, e.target.value)}
                                  style={{ width: 110, textAlign: 'right', fontSize: 12 }} />
                                <button className="btn btn-outline btn-sm" style={{ padding: '4px 10px', fontSize: 12, color: '#dc3545' }}
                                  onClick={() => deleteCompAlloc(alloc.id)}>✕</button>
                              </div>
                            ))}
                            {canEdit && (
                              <button className="btn btn-outline btn-sm" style={{ fontSize: 12, color: '#1d6fa8', marginTop: 8 }}
                                onClick={() => addCompAlloc(comp.compKey)}>+ {t('prices.addProduct')}</button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ DEALS ═══ */}
      {tab === 'deals' && (
        <div>
          <div className="flex-between mb-16">
            <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1 }}>{he ? 'דילים' : 'DEALS'}</h2>
            {canEdit && (
              <button className="btn btn-gold" onClick={async () => {
                const name = prompt('Deal name (e.g. "22K Deal", "Home Line Compensation"):');
                if (!name) return;
                const type = (await confirm({
                  title: he ? 'סוג דיל' : 'Deal type',
                  body: he ? 'דיל בכמות קבועה או דיל מתמשך?' : 'Fixed quantity deal or ongoing?',
                  confirmLabel: he ? 'קבוע' : 'Fixed',
                  cancelLabel: he ? 'מתמשך' : 'Ongoing',
                })) ? 'fixed' : 'ongoing';
                const totalSupport = type === 'fixed' ? parseFloat(prompt('Total support amount ($):') || 0) : 0;
                const mdfAmount = type === 'fixed' ? parseFloat(prompt('MDF amount ($):') || 0) : 0;
                const straightDiscount = type === 'fixed' ? parseFloat(prompt('Straight discount ($):') || 0) : 0;
                const shareInput = prompt(he ? 'חלק Shager מהדיסקאונט (1 = הכל, 0.6667 = 2/3):' : 'Shager share of discount (1 = all, 0.6667 = 2/3):', '1');
                const shagerShareRatio = shareInput ? parseFloat(shareInput) : 1;

                const productCount = parseInt(prompt('How many products in this deal?') || 0);
                if (!productCount) return;
                const products = [];
                for (let i = 0; i < productCount; i++) {
                  const sku = prompt(`Product ${i+1} SKU:`);
                  if (!sku) continue;
                  const description = prompt(`Product ${i+1} name:`);
                  const originalPrice = parseFloat(prompt(`Product ${i+1} original price ($):`) || 0);
                  const promoPrice = parseFloat(prompt(`Product ${i+1} promo price ($):`) || 0);
                  const dealQty = type === 'fixed' ? parseFloat(prompt(`Product ${i+1} deal qty:`) || 0) : null;
                  products.push({ sku, description, originalPrice, promoPrice, dealQty });
                }
                if (!products.length) return;
                try {
                  await api.post('/prices/deals', { name, type, totalSupport, mdfAmount, straightDiscount, shagerShareRatio, products });
                  fetchData(true);
                } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
              }}>{he ? '+ דיל חדש' : '+ New Deal'}</button>
            )}
          </div>

          {(() => {
            if (deals.length === 0) return <div className="empty-state">{t('common.noData')}</div>;
            const activeDeals = deals.filter(d => d.status !== 'closed');
            const closedDeals = deals.filter(d => d.status === 'closed');

            const renderDealRow = (deal) => {
              const isOpen = expandedDeals.has(deal.id);
              const totalDealValue = deal.totalDealValue || 0;
              const earnedPct = totalDealValue > 0 ? Math.round((deal.totalCreditEarned || 0) / totalDealValue * 100) : 0;
              const isClosed = deal.status === 'closed';
              const periodLabel = (deal.effectiveFrom || deal.effectiveTo)
                ? `${deal.effectiveFrom ? new Date(deal.effectiveFrom).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }) : '—'} → ${deal.effectiveTo ? new Date(deal.effectiveTo).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }) : '—'}`
                : null;
              // GRID: arrow | name-and-badges (flex-1) | 4 metrics (fixed 140px each = perfect alignment across rows)
              return (
                <div key={deal.id} className="order-group" style={{ marginBottom: 8 }}>
                  <div
                    className="order-group-header"
                    onClick={() => setExpandedDeals(prev => { const s = new Set(prev); s.has(deal.id) ? s.delete(deal.id) : s.add(deal.id); return s; })}
                    style={{
                      cursor: 'pointer',
                      display: 'grid',
                      gridTemplateColumns: '30px 1fr 140px 140px 140px 140px',
                      gap: 12,
                      alignItems: 'center',
                      padding: '10px 14px',
                      fontSize: 13,
                      minHeight: 56,
                      opacity: isClosed ? 0.9 : 1,
                    }}
                  >
                    <span className={`arrow${isOpen ? ' open' : ''}`}>&#9654;</span>

                    {/* Name + period + all badges together — one flexible slot */}
                    <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="fw-bold" style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                          {deal.name}
                        </div>
                        {periodLabel && (
                          <div className="text-muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                            📅 {periodLabel}
                          </div>
                        )}
                      </div>
                      <span className="badge" style={{ background: deal.type === 'ongoing' ? '#e3f2fd' : '#fff3e0', color: deal.type === 'ongoing' ? '#1565c0' : '#e65100', whiteSpace: 'nowrap', fontSize: 11 }}>
                        {deal.type === 'ongoing' ? 'Ongoing' : 'Fixed'}
                      </span>
                      {isClosed && (
                        <span className="badge" style={{ background: '#e0e7ff', color: '#3730a3', whiteSpace: 'nowrap', fontSize: 11, fontWeight: 700 }}>🔒 {he ? 'סגור' : 'CLOSED'}</span>
                      )}
                      {deal.targetReached && !isClosed && (
                        <span className="badge" style={{ background: '#dcfce7', color: '#166534', whiteSpace: 'nowrap', fontSize: 11, fontWeight: 700 }}>✓ {he ? 'הושלם' : 'REACHED'}</span>
                      )}
                      {deal.shagerShareRatio && deal.shagerShareRatio !== 1 && (
                        <span className="badge" style={{ background: '#fef3c7', color: '#92400e', whiteSpace: 'nowrap', fontSize: 11 }} title={he ? 'חלק Shager' : 'Shager share'}>
                          {Math.round(deal.shagerShareRatio * 100)}%
                        </span>
                      )}
                    </div>

                    {/* 4 fixed-width metric columns — perfect vertical alignment across all rows */}
                    <DealMetric label={he ? 'סה"כ' : 'Total'} value={fmt(totalDealValue)} color="#1565c0" sub={`${deal.products.length} ${he ? 'מוצרים' : 'products'}`} />
                    <DealMetric label={he ? 'הרווחנו' : 'Earned'} value={fmt(deal.totalCreditEarned || 0)} color="#f59e0b" sub={totalDealValue > 0 ? `${earnedPct}%` : null} />
                    <DealMetric label={he ? 'התקבל' : 'Received'} value={fmt(deal.totalCreditReceived)} color="#16a34a" />
                    {/* Owed — active: red text only; closed: red text on red background */}
                    <DealMetric
                      label={he ? 'חוב' : 'Owed'}
                      value={fmt(deal.owedAmount || 0)}
                      color={(deal.owedAmount || 0) > 0 ? '#dc2626' : '#6b7280'}
                      highlight={isClosed && (deal.owedAmount || 0) > 0}
                    />
                  </div>

                {isOpen && (
                  <div className="order-group-body" style={{ padding: 16 }}>
                    {/* Products by model — expandable */}
                    {(() => {
                      // Use backend-provided modelGroups (pooled caps per model)
                      const modelList = deal.modelGroups || [];
                      const shareRatio = deal.shagerShareRatio || 1;
                      const totalUsedCreditNet = modelList.reduce((s, m) => s + (m.earnedNet || 0), 0);
                      const totalDealBudgetNet = deal.totalDealValue || modelList.reduce((s, m) => s + (m.valueNet || 0), 0);
                      const usedPctNet = totalDealBudgetNet > 0 ? Math.round(totalUsedCreditNet / totalDealBudgetNet * 100) : 0;

                      return (
                        <div style={{ marginBottom: 16 }}>
                          {/* Deal usage progress — all values NET (Shager's share) */}
                          <div style={{ marginBottom: 12, padding: 12, background: '#f8f9fa', borderRadius: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                              <span>{he ? 'ניצול הדיל' : 'Deal Usage'}: <strong>{fmt(totalUsedCreditNet)}</strong> / {fmt(totalDealBudgetNet)} <span className="text-muted" style={{ fontSize: 11 }}>({he ? 'נטו לשאגר' : 'net to Shager'})</span></span>
                              <span style={{ fontWeight: 700, color: usedPctNet > 80 ? '#dc2626' : usedPctNet > 50 ? '#f59e0b' : '#16a34a' }}>{usedPctNet}%</span>
                            </div>
                            <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${Math.min(usedPctNet, 100)}%`, background: usedPctNet > 80 ? '#dc2626' : usedPctNet > 50 ? '#f59e0b' : '#16a34a', borderRadius: 4, transition: 'width 0.3s' }} />
                            </div>
                          </div>

                          {modelList.map(m => {
                            const mKey = `model-${deal.id}-${m.modelKey}`;
                            const isModelOpen = expandedMonths.has(mKey);
                            const modelPct = m.poolQty > 0 ? Math.round(m.usedPool / m.poolQty * 100) : 0;
                            return (
                              <div key={m.modelKey} style={{ marginBottom: 4 }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(160px, 1.2fr) 90px minmax(200px, 1fr) minmax(130px, auto) minmax(110px, auto)', gap: 12, alignItems: 'center', padding: '8px 12px', background: '#f8f9fa', borderRadius: 6, cursor: 'pointer' }}
                                  onClick={() => setExpandedMonths(prev => { const s = new Set(prev); s.has(mKey) ? s.delete(mKey) : s.add(mKey); return s; })}>
                                  <span className={`arrow${isModelOpen ? ' open' : ''}`} style={{ fontSize: 10 }}>&#9654;</span>
                                  <span className="fw-bold" style={{ fontSize: 13 }}>{m.modelKey}</span>
                                  <span style={{ fontSize: 12, color: '#4caf50' }}>${m.promoPrice}</span>
                                  <span className="text-muted" style={{ fontSize: 11 }}>
                                    {he ? 'מקורי' : 'was'} ${m.originalPrice} · {he ? 'נטו' : 'net'} {fmt(m.creditPerUnitNet)}/u{shareRatio !== 1 ? ` · ${he ? 'ברוטו' : 'gross'} ${fmt(m.creditPerUnitGross)}` : ''} · {m.skus.length} SKU
                                  </span>
                                  {deal.type === 'fixed' && m.poolQty > 0 ? (
                                    <span style={{ fontSize: 12, textAlign: 'right' }}>
                                      <strong>{fmtQty(m.usedPool)}</strong> / {fmtQty(m.poolQty)}
                                      <span style={{ marginInlineStart: 6, fontWeight: 700, color: modelPct >= 100 ? '#16a34a' : modelPct > 80 ? '#dc2626' : '#f59e0b' }}>{modelPct}%</span>
                                    </span>
                                  ) : <span />}
                                  <span className="text-danger fw-bold" style={{ textAlign: 'right' }}>{fmt(m.earnedNet)}</span>
                                </div>
                                {isModelOpen && (
                                  <div className="table-wrap" style={{ marginTop: 4, marginBottom: 8 }}>
                                    <table>
                                      <thead><tr>
                                        <th>{t('common.sku')}</th><th>{t('common.product')}</th>
                                        <th className="text-end">{he ? 'נמכר' : 'Used'}</th>
                                      </tr></thead>
                                      <tbody>
                                        {m.skus.map(p => (
                                          <tr key={p.sku}>
                                            <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{p.sku}</td>
                                            <td>{p.description}</td>
                                            <td className="text-end fw-bold">{fmtQty(p.usedQty)}</td>
                                          </tr>
                                        ))}
                                        <tr style={{ borderTop: '1px solid #dee2e6', fontWeight: 700 }}>
                                          <td colSpan="2">{he ? 'סה"כ דגם (מאגר משותף)' : 'Model Total (pooled)'}</td>
                                          <td className="text-end">{fmtQty(m.usedPool)} / {fmtQty(m.poolQty)}</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* Monthly breakdown — expandable */}
                    {deal.monthlyBreakdown.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <h4 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 8 }}>{he ? 'זיכוי צפוי לפי חודש' : 'Credit Due by Month'}</h4>
                        {deal.monthlyBreakdown.map(m => {
                          const mKey = `${deal.id}-${m.month}`;
                          const mOpen = expandedMonths.has(mKey);
                          return (
                            <div key={m.month} style={{ marginBottom: 4 }}>
                              <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '6px 12px', background: '#f8f9fa', borderRadius: 6, cursor: 'pointer' }}
                                onClick={() => setExpandedMonths(prev => { const s = new Set(prev); s.has(mKey) ? s.delete(mKey) : s.add(mKey); return s; })}>
                                <span className={`arrow${mOpen ? ' open' : ''}`} style={{ fontSize: 10 }}>&#9654;</span>
                                <span className="fw-bold">{m.month}</span>
                                <span className="text-muted">{m.items.length} items</span>
                                <div style={{ flex: 1 }} />
                                <span className="text-danger fw-bold">{fmt(m.totalCredit)}</span>
                              </div>
                              {mOpen && (
                                <div className="table-wrap" style={{ marginTop: 4 }}>
                                  <table>
                                    <thead><tr>
                                      <th>Order (SI)</th><th>{t('common.sku')}</th><th>{t('common.product')}</th>
                                      <th className="text-end">{t('warehouse.qty')}</th><th className="text-end">Original</th>
                                      <th className="text-end">Promo</th><th className="text-end">Credit</th>
                                    </tr></thead>
                                    <tbody>
                                      {m.items.map((item, i) => (
                                        <tr key={i}>
                                          <td className="text-gold">{item.orderNo}</td>
                                          <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{item.sku}{item.barcode && item.barcode !== item.sku && <div className="text-muted" style={{ fontSize: 10, fontFamily: 'Arial,sans-serif' }}>{item.barcode}</div>}</td>
                                          <td>{item.description}</td>
                                          <td className="text-end fw-bold">{fmtQty(item.qty)}</td>
                                          <td className="text-end">{fmt(item.originalPrice)}</td>
                                          <td className="text-end">{fmt(item.promoPrice)}</td>
                                          <td className="text-end text-danger fw-bold">{fmt(item.credit)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Credit tracking */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                        <h4 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-dim)', margin: 0 }}>{he ? 'זיכויים שהתקבלו' : 'Credits Received'}</h4>
                        {canEdit && (
                          <button className="btn btn-sm btn-gold" onClick={async () => {
                            const amount = parseFloat(prompt('Credit amount received ($):') || 0);
                            if (!amount) return;
                            const creditType = prompt('Type (e.g. "straight", "mdf", "monthly"):') || 'credit';
                            const description = prompt('Description:') || '';
                            const orderNo = prompt('Order/Invoice No (optional):') || '';
                            try {
                              await api.post(`/prices/deals/${deal.id}/credit`, { creditType, amount, description, orderNo });
                              fetchData(true);
                            } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                          }}>+ Add Credit</button>
                        )}
                      </div>
                      {deal.credits.length === 0 ? <span className="text-muted">No credits received yet</span> : (
                        <div className="table-wrap">
                          <table>
                            <thead><tr><th>Type</th><th>Description</th><th>Order</th><th className="text-end">Amount</th>{canEdit && <th></th>}</tr></thead>
                            <tbody>
                              {deal.credits.map(c => (
                                <tr key={c.id}>
                                  <td><span className="badge">{c.type}</span></td>
                                  <td>{c.description || '—'}</td>
                                  <td className="text-gold">{c.orderNo || '—'}</td>
                                  <td className="text-end fw-bold text-success">{fmt(c.amount)}</td>
                                  {canEdit && <td><button className="btn btn-sm btn-danger" onClick={async () => { if (await confirm({ body: he ? 'למחוק זיכוי זה?' : 'Delete this credit?', danger: true })) { await api.delete(`/prices/deals/credit/${c.id}`); fetchData(true); } }}>{t('common.delete')}</button></td>}
                                </tr>
                              ))}
                              <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                                <td colSpan={3}>{t('common.total')}</td>
                                <td className="text-end text-success">{fmt(deal.totalCreditReceived)}</td>
                                {canEdit && <td></td>}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Period editor — always visible, editable while deal is active */}
                    <div className="card" style={{ padding: 12, background: '#eef2ff', marginBottom: 8 }}>
                      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#3730a3', textTransform: 'uppercase' }}>
                          {he ? 'תקופת הדיל' : 'Deal Period'}
                        </span>
                        <div>
                          <label style={{ fontSize: 11, color: '#6b7280', display: 'block' }}>{he ? 'התחלה' : 'From'}</label>
                          <input
                            type="date"
                            className="input input-sm"
                            style={{ width: 150 }}
                            disabled={!canEdit || deal.status === 'closed'}
                            defaultValue={deal.effectiveFrom ? String(deal.effectiveFrom).slice(0, 10) : ''}
                            onBlur={async e => {
                              const val = e.target.value || null;
                              const current = deal.effectiveFrom ? String(deal.effectiveFrom).slice(0, 10) : null;
                              if (val === current) return;
                              try { await api.patch(`/prices/deals/${deal.id}`, { effectiveFrom: val }); fetchData(true); }
                              catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#6b7280', display: 'block' }}>{he ? 'סיום' : 'To'}</label>
                          <input
                            type="date"
                            className="input input-sm"
                            style={{ width: 150 }}
                            disabled={!canEdit || deal.status === 'closed'}
                            defaultValue={deal.effectiveTo ? String(deal.effectiveTo).slice(0, 10) : ''}
                            onBlur={async e => {
                              const val = e.target.value || null;
                              const current = deal.effectiveTo ? String(deal.effectiveTo).slice(0, 10) : null;
                              if (val === current) return;
                              try { await api.patch(`/prices/deals/${deal.id}`, { effectiveTo: val }); fetchData(true); }
                              catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                            }}
                          />
                        </div>
                        <span className="text-muted" style={{ fontSize: 12, flex: 1 }}>
                          {he ? 'רק SI בתקופה זו ייספרו לדיל. ריק = ללא הגבלה' : 'Only SI in this window counts. Empty = no limit.'}
                        </span>
                      </div>
                    </div>

                    {/* Summary bar */}
                    <div className="card" style={{ padding: 12, background: '#f8f9fa' }}>
                      <div style={{ display: 'flex', gap: 24, justifyContent: 'flex-end', fontSize: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span>{he ? 'סה"כ דיל' : 'Deal Total'}: <strong style={{ color: '#1565c0' }}>{fmt(deal.totalDealValue || 0)}</strong></span>
                        <span>{he ? 'הרווחנו (SI)' : 'Earned (SI)'}: <strong style={{ color: '#f59e0b' }}>{fmt(deal.totalCreditEarned || 0)}</strong></span>
                        <span>{he ? 'התקבל' : 'Received'}: <strong className="text-success">{fmt(deal.totalCreditReceived)}</strong></span>
                        <span>
                          {he ? 'חוב פתוח' : 'Owed to us'}: <strong style={{ color: (deal.owedAmount || 0) > 0 ? '#dc2626' : '#16a34a' }}>{fmt(deal.owedAmount || 0)}</strong>
                        </span>
                        {canEdit && (
                          <button className="btn btn-sm" onClick={async () => {
                            const input = prompt(he ? 'חלק Shager מהדיסקאונט (1 = הכל, 0.6667 = 2/3):' : 'Shager share (1 = all, 0.6667 = 2/3):', String(deal.shagerShareRatio || 1));
                            if (input == null) return;
                            const ratio = parseFloat(input);
                            if (isNaN(ratio) || ratio < 0 || ratio > 1) return alert('Must be between 0 and 1');
                            try { await api.patch(`/prices/deals/${deal.id}`, { shagerShareRatio: ratio }); fetchData(true); }
                            catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                          }}>{he ? 'ערוך חלק' : 'Edit Share'}: {Math.round((deal.shagerShareRatio || 1) * 100)}%</button>
                        )}
                        {canEdit && deal.status !== 'closed' && (
                          <button className="btn btn-sm" style={{ background: '#3730a3', color: '#fff', border: 'none' }} onClick={async () => {
                            // Use already-set effective dates. If effective_to missing, default to today.
                            const today = new Date().toISOString().slice(0, 10);
                            const effectiveTo = deal.effectiveTo ? String(deal.effectiveTo).slice(0, 10) : today;
                            const effectiveFrom = deal.effectiveFrom ? String(deal.effectiveFrom).slice(0, 10) : null;
                            if (!(await confirm({
                              title: he ? 'סיום דיל' : 'Finish deal',
                              body: he
                                ? `לסיים את הדיל "${deal.name}"?\nתקופה: ${effectiveFrom || '—'} → ${effectiveTo}\nSI מחוץ לתקופה לא ייספרו יותר.`
                                : `Finish deal "${deal.name}"?\nPeriod: ${effectiveFrom || '—'} → ${effectiveTo}\nSI outside this period will no longer count.`,
                              danger: true,
                            }))) return;
                            try {
                              await api.post(`/prices/deals/${deal.id}/close`, { effectiveFrom, effectiveTo });
                              fetchData(true);
                            } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                          }}>🔒 {he ? 'סיים דיל' : 'Finish Deal'}</button>
                        )}
                        {canEdit && deal.status === 'closed' && (
                          <button className="btn btn-sm btn-outline" onClick={async () => {
                            if (!(await confirm({ body: he ? `לפתוח מחדש את הדיל "${deal.name}"?` : `Reopen deal "${deal.name}"?` }))) return;
                            try {
                              await api.post(`/prices/deals/${deal.id}/reopen`);
                              fetchData(true);
                            } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                          }}>🔓 {he ? 'פתח מחדש' : 'Reopen'}</button>
                        )}
                      </div>
                    </div>

                    {canEdit && (
                      <div style={{ marginTop: 12, textAlign: 'right' }}>
                        <button className="btn btn-sm btn-danger" onClick={async () => {
                          if (await confirm({ body: he ? `למחוק דיל "${deal.name}"?` : `Delete deal "${deal.name}"?`, danger: true })) {
                            await api.delete(`/prices/deals/${deal.id}`);
                            fetchData(true);
                          }
                        }}>{t('common.delete')} Deal</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
            };

            return (
              <>
                {activeDeals.length === 0 && closedDeals.length === 0 && <div className="empty-state">{t('common.noData')}</div>}
                {activeDeals.map(renderDealRow)}
                {closedDeals.length > 0 && (
                  <>
                    <div style={{ marginTop: 28, marginBottom: 10, paddingBottom: 6, borderBottom: '2px solid #cbd5e1', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#475569', margin: 0 }}>
                        🔒 {he ? 'דילים סגורים' : 'Closed Deals'}
                      </h3>
                      <span className="text-muted" style={{ fontSize: 11 }}>
                        ({closedDeals.length})
                      </span>
                    </div>
                    {closedDeals.map(renderDealRow)}
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* ═══ QUARTERLY SUMMARY (SI Current + SO Expected) ═══ */}
      {tab === 'credit' && (() => {
        const quarters = data?.credits || [];
        const dealTotals = data?.dealTotals || [];
        const he = i18n.language === 'he';

        // MAIN CUBES: SI ONLY (realized — supplier-confirmed)
        const totSupport   = quarters.reduce((s, r) => s + (r.siSupport   || 0), 0);
        const totRMA       = quarters.reduce((s, r) => s + (r.siDefective || 0), 0);
        const hlDeal       = dealTotals.find(d => d.dealName?.includes('Home'));
        const deal22k      = dealTotals.find(d => d.dealName?.includes('22K'));
        const totHL        = hlDeal    ? (hlDeal.siExpected    || 0) : 0;
        const tot22K       = deal22k   ? (deal22k.siExpected   || 0) : 0;
        const hlReceived   = hlDeal?.received     || 0;
        const deal22kReceived = deal22k?.received || 0;
        const totalReceived = creditsReceived.reduce((s, c) => s + c.amount, 0);

        // SO PENDING (not yet realized — shown in a separate section)
        const soSupport  = quarters.reduce((s, r) => s + (r.soSupport   || 0), 0);
        const soRMA      = quarters.reduce((s, r) => s + (r.soDefective || 0), 0);
        const soHL       = hlDeal  ? (hlDeal.soExpected  || 0) : 0;
        const so22K      = deal22k ? (deal22k.soExpected || 0) : 0;
        const totSOPending = soSupport + soRMA + soHL + so22K;

        const cubes = [
          { key: 'support', label: he ? 'תמיכה 2%' : 'Support 2%', total: totSupport, received: totalReceived },
          { key: 'rma', label: he ? 'RMA / פגומים 1.75%' : 'RMA 1.75%', total: totRMA, received: 0 },
          { key: 'hl', label: he ? 'זיכוי Home Line' : 'HL Credit', total: totHL, received: hlReceived },
          { key: '22k', label: he ? 'זיכוי 22K' : '22K Deal', total: tot22K, received: deal22kReceived },
        ];

        const addSupportCredit = async () => {
          if (!newCredit.number.trim()) return;
          await api.post('/prices/credits-received', { type: 'support', quarter: newCredit.quarter || 'ALL', creditNumber: newCredit.number, amount: parseFloat(newCredit.amount) || 0 });
          setNewCredit({ number: '', amount: '' });
          fetchData(true);
        };
        const deleteCredit = async (id) => {
          if (!(await confirm({ body: he ? 'למחוק?' : 'Delete?', danger: true }))) return;
          await api.delete(`/prices/credits-received/${id}`);
          fetchData(true);
        };

        return (
          <div>
            {/* 4 clickable cubes */}
            <div className="summary-cards" style={{ marginBottom: 24, gridTemplateColumns: 'repeat(4, 1fr)' }}>
              {cubes.map(c => {
                const active = creditView === c.key;
                const outstanding = c.total - c.received;
                return (
                  <div key={c.key} className="summary-card clickable" onClick={() => setCreditView(c.key)}
                    style={{ cursor: 'pointer', border: active ? '2px solid #333' : '1px solid #e2e8f0', background: active ? '#f8f9fa' : '#fff', transition: 'all 0.15s' }}>
                    <div className="label" style={{ fontWeight: 700 }}>{c.label}</div>
                    <div className="value">{fmtInt(c.total)}</div>
                    <div className="sub" style={{ color: outstanding > 0 ? '#dc2626' : '#16a34a' }}>
                      {c.received > 0 ? `${he ? 'התקבל' : 'Received'}: ${fmtInt(c.received)}` : (he ? 'טרם התקבל' : 'Not received')}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Detail table based on selected cube */}
            {creditView === 'support' && (
              <div>
                <h3 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, marginBottom: 16 }}>{he ? 'תמיכה 2% (SI בלבד)' : 'SUPPORT 2% (SI ONLY)'}</h3>
                <div className="table-wrap" style={{ marginBottom: 24 }}>
                  <table>
                    <thead><tr>
                      <th>{he ? 'רבעון' : 'Quarter'}</th>
                      <th className="text-end">SI {he ? 'רכישות' : 'Purchases'}</th>
                      <th className="text-end fw-bold">SI × 2%</th>
                    </tr></thead>
                    <tbody>
                      {quarters.map((row, i) => (
                        <tr key={i}>
                          <td className="fw-bold">{row.quarter}</td>
                          <td className="text-end">{fmt(row.siPurchases)}</td>
                          <td className="text-end fw-bold">{fmt(row.siSupport)}</td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 700, borderTop: '2px solid #dee2e6' }}>
                        <td>{t('common.total')}</td>
                        <td className="text-end">{fmt(quarters.reduce((s, r) => s + r.siPurchases, 0))}</td>
                        <td className="text-end">{fmt(totSupport)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {/* Credits received */}
                <h4 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: '#6c757d', marginBottom: 8 }}>{he ? 'זיכויים שהתקבלו' : 'Credits Received'}</h4>
                <div className="table-wrap">
                  <table>
                    <thead><tr>
                      <th>{he ? 'רבעון' : 'Quarter'}</th>
                      <th>{he ? 'מס׳ זיכוי' : 'Credit #'}</th>
                      <th className="text-end">{he ? 'סכום' : 'Amount'}</th>
                      {canEdit && <th style={{ width: 50 }}></th>}
                    </tr></thead>
                    <tbody>
                      {creditsReceived.map(c => (
                        <tr key={c.id}>
                          <td>{c.quarter}</td>
                          <td style={{ fontWeight: 600 }}>{c.creditNumber}</td>
                          <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{fmt(c.amount)}</td>
                          {canEdit && <td><button className="btn btn-sm btn-danger" onClick={() => deleteCredit(c.id)}>✕</button></td>}
                        </tr>
                      ))}
                      {canEdit && (
                        <tr style={{ background: '#f8f9fa' }}>
                          <td>
                            <select className="input input-sm" value={newCredit.quarter || ''} onChange={e => setNewCredit({ ...newCredit, quarter: e.target.value })} style={{ width: 100 }}>
                              <option value="">—</option>
                              {quarters.map(q => <option key={q.quarter} value={q.quarter}>{q.quarter}</option>)}
                            </select>
                          </td>
                          <td><input className="input input-sm" placeholder={he ? 'מס׳ זיכוי...' : 'Credit #...'} value={newCredit.number} onChange={e => setNewCredit({ ...newCredit, number: e.target.value })} onKeyDown={e => e.key === 'Enter' && addSupportCredit()} /></td>
                          <td className="text-end"><input className="input input-sm" type="number" step="0.01" placeholder="$" style={{ width: 120, textAlign: 'right' }} value={newCredit.amount} onChange={e => setNewCredit({ ...newCredit, amount: e.target.value })} onKeyDown={e => e.key === 'Enter' && addSupportCredit()} /></td>
                          {canEdit && <td><button className="btn btn-sm btn-gold" onClick={addSupportCredit}>+</button></td>}
                        </tr>
                      )}
                      {creditsReceived.length > 0 && (
                        <tr style={{ fontWeight: 700, borderTop: '2px solid #dee2e6' }}>
                          <td colSpan={2}>{t('common.total')}</td>
                          <td className="text-end">{fmt(totalReceived)}</td>
                          {canEdit && <td></td>}
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {creditView === 'rma' && (
              <div>
                <h3 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, marginBottom: 16 }}>{he ? 'RMA / פגומים 1.75% (SI בלבד)' : 'RMA / DEFECTIVE 1.75% (SI ONLY)'}</h3>
                <div className="table-wrap">
                  <table>
                    <thead><tr>
                      <th>{he ? 'רבעון' : 'Quarter'}</th>
                      <th className="text-end">SI {he ? 'רכישות' : 'Purchases'}</th>
                      <th className="text-end fw-bold">SI × 1.75%</th>
                    </tr></thead>
                    <tbody>
                      {quarters.map((row, i) => (
                        <tr key={i}>
                          <td className="fw-bold">{row.quarter}</td>
                          <td className="text-end">{fmt(row.siPurchases)}</td>
                          <td className="text-end fw-bold">{fmt(row.siDefective)}</td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 700, borderTop: '2px solid #dee2e6' }}>
                        <td>{t('common.total')}</td>
                        <td className="text-end">{fmt(quarters.reduce((s, r) => s + r.siPurchases, 0))}</td>
                        <td className="text-end">{fmt(totRMA)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Q2+ Price Reduction Verification */}
                <h4 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', marginTop: 24, marginBottom: 8 }}>
                  {he ? 'בדיקת הפחתת 1.75% מ-Q2 ואילך' : 'Q2+ Price Reduction Verification (1.75%)'}
                </h4>
                <p className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>
                  {he ? 'מ-Q2 ה-1.75% כבר כלול במחיר המוצר. הטבלה מראה אם המחיר ירד ב-1.75% בהשוואה ל-Q1.' : 'From Q2 the 1.75% is included in product price. This table verifies the price was reduced by ~1.75% compared to Q1.'}
                </p>
                {data?.rmaCheck && data.rmaCheck.length > 0 ? (
                  <div className="table-wrap">
                    <table>
                      <thead><tr>
                        <th>{he ? 'מק"ט' : 'SKU'}</th>
                        <th>{he ? 'מוצר' : 'Product'}</th>
                        <th className="text-end">Q1 {he ? 'מחיר' : 'Price'}</th>
                        <th className="text-end">Q2+ {he ? 'מחיר' : 'Price'}</th>
                        <th className="text-end">{he ? 'הפחתה' : 'Reduction'}</th>
                        <th className="text-center">{he ? 'בדיקה' : 'Check'}</th>
                      </tr></thead>
                      <tbody>
                        {data.rmaCheck.map(r => (
                          <tr key={r.sku} style={!r.verified ? { background: '#fef2f2' } : {}}>
                            <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{r.sku}</td>
                            <td>{r.description}</td>
                            <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmt(r.q1Price)}</td>
                            <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmt(r.q2Price)}</td>
                            <td className="text-end fw-bold" style={{ color: r.reductionPct > 0 ? '#16a34a' : '#dc2626' }}>{r.reductionPct}%</td>
                            <td className="text-center">{r.verified ? '✅' : '❌'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="text-muted">{he ? 'אין נתונים להשוואה עדיין' : 'No Q1 vs Q2 data to compare yet'}</div>}
              </div>
            )}

            {(creditView === 'hl' || creditView === '22k') && (() => {
              const dealName = creditView === 'hl' ? 'Home Line Compensation' : '22K Deal';
              const label = creditView === 'hl' ? (he ? 'זיכוי Home Line' : 'HOME LINE CREDIT') : (he ? 'זיכוי 22K' : '22K DEAL CREDIT');
              const dt = dealTotals.find(d => d.dealName === dealName);
              const received = dt?.received || 0;
              return (
                <div>
                  <h3 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, marginBottom: 16 }}>{label} <span className="text-muted" style={{ fontSize: 12, fontWeight: 400 }}>({he ? 'SI בלבד' : 'SI Only'})</span></h3>
                  <div className="table-wrap" style={{ marginBottom: 24 }}>
                    <table>
                      <thead><tr>
                        <th>{he ? 'רבעון' : 'Quarter'}</th>
                        <th className="text-end fw-bold">SI {he ? 'זיכוי' : 'Credit'}</th>
                      </tr></thead>
                      <tbody>
                        {quarters.map((row, i) => {
                          const dc = (row.dealCredits || []).find(d => d.dealName === dealName);
                          if (!dc || !dc.siCredit) return null;
                          return (
                            <tr key={i}>
                              <td className="fw-bold">{row.quarter}</td>
                              <td className="text-end fw-bold">{fmt(dc.siCredit)}</td>
                            </tr>
                          );
                        })}
                        <tr style={{ fontWeight: 700, borderTop: '2px solid #dee2e6' }}>
                          <td>{t('common.total')}</td>
                          <td className="text-end">{fmt(creditView === 'hl' ? totHL : tot22K)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {received > 0 && (
                    <div style={{ padding: 16, background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                      <span style={{ fontWeight: 700 }}>{he ? 'התקבל' : 'Received'}: </span>
                      <span style={{ fontWeight: 700, color: '#16a34a', fontSize: 18 }}>{fmt(received)}</span>
                      <span className="text-muted" style={{ marginInlineStart: 12 }}>
                        {he ? 'יתרה' : 'Outstanding'}: {fmt((creditView === 'hl' ? totHL : tot22K) - received)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ═══ SO CREDITS — Pending (not yet realized, SO orders) ═══ */}
            {totSOPending > 0 && (
              <div style={{ marginTop: 40, padding: 16, background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <h3 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, margin: 0, color: '#9a3412' }}>
                    ⏳ {he ? 'זיכויי SO — ממתינים' : 'SO CREDITS — Pending'}
                  </h3>
                  <span className="text-muted" style={{ fontSize: 11 }}>
                    {he ? 'הזמנות SO שעדיין לא הפכו ל-SI. הזיכוי לא ממומש עד שנשלח בפועל.' : 'SO orders not yet shipped (SI). Credit is not realized until shipment.'}
                  </span>
                </div>
                <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 16 }}>
                  <div className="summary-card" style={{ background: '#fffbeb' }}>
                    <div className="label">{he ? 'תמיכה 2% (SO)' : 'Support 2% (SO)'}</div>
                    <div className="value" style={{ color: '#b45309' }}>{fmtInt(soSupport)}</div>
                    <div className="sub">{he ? 'ממתין' : 'pending'}</div>
                  </div>
                  <div className="summary-card" style={{ background: '#fffbeb' }}>
                    <div className="label">{he ? 'RMA 1.75% (SO)' : 'RMA 1.75% (SO)'}</div>
                    <div className="value" style={{ color: '#b45309' }}>{fmtInt(soRMA)}</div>
                    <div className="sub">{he ? 'ממתין' : 'pending'}</div>
                  </div>
                  <div className="summary-card" style={{ background: '#fffbeb' }}>
                    <div className="label">{he ? 'HL (SO)' : 'HL (SO)'}</div>
                    <div className="value" style={{ color: '#b45309' }}>{fmtInt(soHL)}</div>
                    <div className="sub">{he ? 'ממתין' : 'pending'}</div>
                  </div>
                  <div className="summary-card" style={{ background: '#fffbeb' }}>
                    <div className="label">{he ? '22K (SO)' : '22K (SO)'}</div>
                    <div className="value" style={{ color: '#b45309' }}>{fmtInt(so22K)}</div>
                    <div className="sub">{he ? 'ממתין' : 'pending'}</div>
                  </div>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead><tr>
                      <th>{he ? 'רבעון' : 'Quarter'}</th>
                      <th className="text-end">SO {he ? 'רכישות' : 'Purchases'}</th>
                      <th className="text-end">SO × 2%</th>
                      <th className="text-end">SO × 1.75%</th>
                      <th className="text-end">SO HL</th>
                      <th className="text-end">SO 22K</th>
                    </tr></thead>
                    <tbody>
                      {quarters.filter(r => (r.soPurchases || r.soSupport || r.soDefective || 0) > 0 ||
                        (r.dealCredits || []).some(d => d.soCredit > 0)).map((row, i) => {
                        const hlDC = (row.dealCredits || []).find(d => d.dealName?.includes('Home'));
                        const k22DC = (row.dealCredits || []).find(d => d.dealName?.includes('22K'));
                        return (
                          <tr key={i}>
                            <td className="fw-bold">{row.quarter}</td>
                            <td className="text-end">{fmt(row.soPurchases || 0)}</td>
                            <td className="text-end">{fmt(row.soSupport || 0)}</td>
                            <td className="text-end">{fmt(row.soDefective || 0)}</td>
                            <td className="text-end">{fmt(hlDC?.soCredit || 0)}</td>
                            <td className="text-end">{fmt(k22DC?.soCredit || 0)}</td>
                          </tr>
                        );
                      })}
                      <tr style={{ fontWeight: 700, borderTop: '2px solid #fdba74', background: '#fef3c7' }}>
                        <td>{t('common.total')}</td>
                        <td className="text-end">{fmt(quarters.reduce((s, r) => s + (r.soPurchases || 0), 0))}</td>
                        <td className="text-end">{fmt(soSupport)}</td>
                        <td className="text-end">{fmt(soRMA)}</td>
                        <td className="text-end">{fmt(soHL)}</td>
                        <td className="text-end">{fmt(so22K)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 8, textAlign: 'right', fontWeight: 700, color: '#9a3412' }}>
                  {he ? 'סה"כ ממתין (לא ממומש)' : 'Total Pending (not realized)'}: <span style={{ fontSize: 18 }}>{fmtInt(totSOPending)}</span>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══ MARKETING EXPENSES TAB ═══ */}
      {tab === 'marketing' && !marketing && !loading && <div className="empty-state">Failed to load. Try refreshing.</div>}
      {tab === 'marketing' && marketing && (() => {
        const { siTotal, budget, carryover, totalAvailable, fixed, fixedTotal, variable, variableTotal, totalExpenses, marshallShare, shagerShare, balance, locked } = marketing;
        const he = i18n.language === 'he';
        const isLocked = locked && canEdit;

        const addExpense = async () => {
          if (!mktNewDesc.trim()) return;
          await api.post('/prices/marketing-expenses', { quarter: mktQuarter, description: mktNewDesc, amount: parseFloat(mktNewAmount) || 0, splitType: mktNewSplit });
          setMktNewDesc(''); setMktNewAmount(''); setMktNewSplit('50/50');
          fetchData(true);
        };

        const deleteExpense = async (id) => {
          if (!(await confirm({ body: he ? 'למחוק שורה?' : 'Delete this row?', danger: true }))) return;
          await api.delete(`/prices/marketing-expenses/${id}`);
          fetchData(true);
        };

        const updateExpense = async (id, field, value) => {
          await api.put(`/prices/marketing-expenses/${id}`, { [field]: field === 'amount' ? parseFloat(value) || 0 : value });
          fetchData(true);
        };

        const exportPdf = () => {
          const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
          const fmtUSD = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
          // XSS-safe: escape user-controlled strings (descriptions come from DB and may
          // contain HTML if a privileged user planted malicious content). Fix 2026-05-27.
          const esc = (s) => String(s == null ? '' : s).replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]));
          const allRows = [...fixed.map(f => ({ desc: f.description, amount: f.amount, split: '50/50', marshall: f.amount / 2 })), ...variable.map(v => ({ desc: v.description, amount: v.amount, split: v.splitType || '50/50', marshall: v.splitType === '100%' ? v.amount : v.amount / 2 }))];
          const w = window.open('', '_blank', 'width=900,height=700');
          w.document.write(`<!DOCTYPE html><html><head><title>Marketing Expenses - ${esc(mktQuarter)}</title>
<style>
  body { font-family: Arial, sans-serif; padding: 50px; color: #1a1d23; }
  h1 { font-size: 24px; border-bottom: 3px solid #1a1d23; padding-bottom: 12px; margin-bottom: 8px; }
  .meta { color: #666; font-size: 13px; margin-bottom: 30px; }
  table { width: 100%; border-collapse: collapse; margin-top: 20px; }
  th { text-align: left; padding: 10px 14px; background: #f4f5f7; border: 1px solid #dee2e6; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #666; }
  td { padding: 10px 14px; border: 1px solid #dee2e6; font-size: 14px; }
  td.right { text-align: right; font-weight: 600; }
  .summary { margin-top: 30px; }
  .summary table td { border: none; padding: 6px 14px; }
  .summary .label { font-weight: 600; color: #666; }
  .summary .total { font-size: 18px; font-weight: 800; border-top: 2px solid #1a1d23; padding-top: 10px; }
  .footer { margin-top: 50px; font-size: 11px; color: #999; border-top: 1px solid #eee; padding-top: 12px; }
  @media print { body { padding: 30px; } }
</style></head><body>
<h1>Marketing Expenses Report — ${esc(mktQuarter)}</h1>
<div class="meta">Company: Shager &nbsp;|&nbsp; Date: ${esc(today)} &nbsp;|&nbsp; Quarter: ${esc(mktQuarter)}</div>

<table>
  <thead><tr><th>#</th><th>Description</th><th>Split</th><th style="text-align:right">Amount</th><th style="text-align:right">Marshall</th></tr></thead>
  <tbody>
    ${allRows.map((r, i) => `<tr><td>${i + 1}</td><td>${esc(r.desc)}</td><td>${esc(r.split)}</td><td class="right">${fmtUSD(r.amount)}</td><td class="right">${fmtUSD(r.marshall)}</td></tr>`).join('')}
    <tr style="font-weight:800;border-top:2px solid #1a1d23"><td colspan="3">Total Expenses</td><td class="right">${fmtUSD(totalExpenses)}</td><td class="right" style="font-size:16px">${fmtUSD(marshallShare)}</td></tr>
  </tbody>
</table>

<div class="summary"><table>
  <tr><td class="label">Total SI Purchases (${esc(mktQuarter)})</td><td class="right">${fmtUSD(siTotal)}</td></tr>
  <tr><td class="label">S&T Marketing Budget (4.5%)</td><td class="right">${fmtUSD(marketing.stBudget || budget * 0.692)}</td></tr>
  <tr><td class="label">Marshall Marketing Budget (2%)</td><td class="right">${fmtUSD(marketing.marshallBudget || budget * 0.308)}</td></tr>
  <tr><td class="label" style="font-weight:700">Total Marketing Budget (6.5%)</td><td class="right" style="font-weight:700">${fmtUSD(budget)}</td></tr>
  ${carryover !== 0 ? `<tr><td class="label">Carryover from previous quarters</td><td class="right">${fmtUSD(carryover)}</td></tr>
  <tr><td class="label" style="font-weight:700">Total Available Budget</td><td class="right" style="font-weight:700">${fmtUSD(totalAvailable)}</td></tr>` : ''}
  <tr><td class="label">Total Expenses</td><td class="right">${fmtUSD(totalExpenses)}</td></tr>
  <tr><td class="label">50/50 Shared Expenses</td><td class="right">${fmtUSD(marketing.shared5050)}</td></tr>
  <tr><td class="label">100% Marshall Expenses</td><td class="right">${fmtUSD(marketing.marshall100)}</td></tr>
  ${(marketing.mdfRows || []).length > 0 ? (marketing.mdfRows || []).map(m => `<tr><td class="label">MDF: ${esc(m.description)}</td><td class="right">${fmtUSD(m.amount)}</td></tr>`).join('') + `<tr><td class="label">MDF Total</td><td class="right">${fmtUSD(marketing.mdfTotal)}</td></tr>` : ''}
  <tr><td class="label total">Total Marshall Contribution</td><td class="right total">${fmtUSD(marshallShare + (marketing.mdfTotal || 0))}</td></tr>
  <tr><td class="label">Remaining Balance (carries to next quarter)</td><td class="right" style="color:${balance >= 0 ? '#2e7d32' : '#c62828'}">${fmtUSD(balance)}</td></tr>
</table></div>

<div class="footer">Generated by SCM Pro · ${esc(today)}</div>
</body></html>`);
          w.document.close();
          setTimeout(() => w.print(), 300);
          // Auto-lock after PDF export
          if (!locked) {
            api.put('/prices/marketing-lock', { quarter: mktQuarter }).then(() => fetchData(true));
          }
        };

        const toggleLock = async () => {
          if (locked) {
            await api.delete(`/prices/marketing-lock/${encodeURIComponent(mktQuarter)}`);
          } else {
            await api.put('/prices/marketing-lock', { quarter: mktQuarter });
          }
          fetchData(true);
        };

        return (
          <div>
            {/* Quarter selector */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center' }}>
              {['Q1', 'Q2', 'Q3', 'Q4'].map(q => {
                const qFull = `${q} ${new Date().getFullYear()}`;
                return (
                  <button key={q} className={`btn btn-sm ${mktQuarter === qFull ? 'btn-gold' : 'btn-outline'}`}
                    onClick={() => setMktQuarter(qFull)}>{qFull}</button>
                );
              })}
              <div style={{ flex: 1 }} />
              {canEdit && (
                <button className={`btn btn-sm ${locked ? 'btn-outline' : 'btn-danger'}`} onClick={toggleLock}
                  style={{ marginRight: 8 }}>
                  {locked ? (he ? '🔓 בטל נעילה' : '🔓 Unlock') : (he ? '🔒 נעל' : '🔒 Lock')}
                </button>
              )}
              <button className="btn btn-outline" onClick={exportPdf}>
                {he ? 'ייצוא PDF' : 'Export PDF'}
              </button>
              <button className="btn btn-outline" onClick={() => {
                const rows = [
                  ...fixed.map(f => ({ Type: 'Fixed', Description: f.description, Amount: f.amount, Split: f.splitType || '50/50', 'Marshall Share': f.amount / 2, 'S&T Share': f.amount / 2 })),
                  ...variable.map(v => ({ Type: 'Variable', Description: v.description, Amount: v.amount, Split: v.splitType || '50/50', 'Marshall Share': v.splitType === '100%' ? v.amount : v.amount / 2, 'S&T Share': v.splitType === '100%' ? 0 : v.amount / 2 })),
                ];
                rows.push({});
                rows.push({ Type: '', Description: 'TOTAL EXPENSES', Amount: totalExpenses, Split: '', 'Marshall Share': marshallShare, 'S&T Share': shagerShare });
                rows.push({});
                rows.push({ Type: '', Description: 'SI Purchases', Amount: siTotal });
                rows.push({ Type: '', Description: 'S&T Budget (4.5%)', Amount: marketing.stBudget || 0 });
                rows.push({ Type: '', Description: 'Marshall Budget (2%)', Amount: marketing.marshallBudget || 0 });
                rows.push({ Type: '', Description: 'Total Budget (6.5%)', Amount: budget });
                if (carryover !== 0) rows.push({ Type: '', Description: 'Carryover', Amount: carryover });
                rows.push({ Type: '', Description: 'BALANCE', Amount: balance });
                const ws = XLSX.utils.json_to_sheet(rows);
                ws['!cols'] = [{wch:10},{wch:35},{wch:14},{wch:10},{wch:14},{wch:14}];
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, `Marketing ${mktQuarter}`);
                XLSX.writeFile(wb, `marketing_expenses_${mktQuarter.replace(' ', '_')}.xlsx`);
              }}>
                📥 Excel
              </button>
              {locked && <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 12 }}>🔒 {he ? 'נעול' : 'Locked'}</span>}
            </div>

            {/* Summary Cards */}
            <div className="summary-cards" style={{ marginBottom: 20 }}>
              <div className="summary-card info">
                <div className="label">{he ? 'סה"כ SI' : 'Total SI'}</div>
                <div className="value">{fmtInt(siTotal)}</div>
                <div className="sub">{mktQuarter}</div>
              </div>
              <div className="summary-card gold">
                <div className="label">{he ? 'תקציב שיווק 6.5%' : 'Marketing Budget 6.5%'}</div>
                <div className="value">{fmtInt(budget)}</div>
                <div className="sub">S&T 4.5%: {fmtInt(marketing.stBudget || 0)} · Marshall 2%: {fmtInt(marketing.marshallBudget || 0)}</div>
              </div>
              {carryover !== 0 && (
                <div className="summary-card" style={{ background: carryover > 0 ? undefined : undefined }}>
                  <div className="label">{he ? 'יתרה מרבעונים קודמים' : 'Carryover'}</div>
                  <div className="value" style={{ color: carryover >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmtInt(carryover)}</div>
                  <div className="sub">{(marketing.carryoverDetails || []).map(d => `${d.quarter}: ${fmtInt(d.balance)}`).join(' · ')}</div>
                </div>
              )}
              <div className="summary-card success">
                <div className="label">{he ? 'סה"כ זמין' : 'Total Available'}</div>
                <div className="value">{fmtInt(totalAvailable)}</div>
                <div className="sub">{he ? 'תקציב + יתרה' : 'budget + carryover'}</div>
              </div>
              <div className="summary-card" style={{ background: 'var(--surface2)' }}>
                <div className="label">{he ? 'MDF מרשל' : 'Marshall MDF'}</div>
                <div className="value">{fmtInt(marketing.mdfTotal || 0)}</div>
                <div className="sub">{he ? 'תקציב נוסף ממרשל' : 'extra from Marshall'}</div>
              </div>
              <div className="summary-card">
                <div className="label">{he ? 'יתרה' : 'Balance'}</div>
                <div className="value" style={{ color: balance >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmtInt(balance)}</div>
                <div className="sub">{he ? 'תקציב - הוצאות' : 'budget - expenses'}</div>
              </div>
            </div>

            {/* All Expenses — Fixed + Variable with Split column */}
            <div className="table-wrap" style={{ marginBottom: 20 }}>
              <table>
                <thead><tr>
                  <th>{he ? 'תיאור' : 'Description'}</th>
                  <th>{he ? 'חלוקה' : 'Split'}</th>
                  <th className="text-end">{he ? 'סכום' : 'Amount'}</th>
                  <th className="text-end">{he ? 'מרשל' : 'Marshall'}</th>
                  {canEdit && <th style={{ width: 50 }}></th>}
                </tr></thead>
                <tbody>
                  {/* Fixed rows */}
                  {fixed.map((f, i) => (
                    <tr key={`f-${i}`} style={{ background: 'var(--surface2)' }}>
                      <td>{f.description} <span className="text-muted" style={{ fontSize: 11 }}>({f.rate})</span></td>
                      <td><span className="badge" style={{ background: '#e3f2fd', color: '#1565c0', fontSize: 11 }}>50/50</span></td>
                      <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{fmtInt(f.amount)}</td>
                      <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: 'var(--green)' }}>{fmtInt(f.amount / 2)}</td>
                      {canEdit && <td></td>}
                    </tr>
                  ))}
                  {/* Variable rows */}
                  {variable.map(v => (
                    <tr key={v.id}>
                      <td>
                        {canEdit && !locked ? (
                          <input className="inline-edit" style={{ width: '100%' }} defaultValue={v.description}
                            onBlur={e => { if (e.target.value !== v.description) updateExpense(v.id, 'description', e.target.value); }} />
                        ) : v.description}
                      </td>
                      <td>
                        {canEdit && !locked ? (
                          <select className="inline-edit" style={{ width: 90 }} value={v.splitType || '50/50'}
                            onChange={e => updateExpense(v.id, 'splitType', e.target.value)}>
                            <option value="50/50">50/50</option>
                            <option value="100%">100% Marshall</option>
                          </select>
                        ) : <span className="badge" style={{ background: v.splitType === '100%' ? '#e8f5e9' : '#e3f2fd', color: v.splitType === '100%' ? '#2e7d32' : '#1565c0', fontSize: 11 }}>{v.splitType === '100%' ? '100% Marshall' : '50/50'}</span>}
                      </td>
                      <td className="text-end">
                        {canEdit && !locked ? (
                          <input className="inline-edit" type="number" step="0.01" style={{ width: 120, textAlign: 'right' }} defaultValue={v.amount}
                            onBlur={e => { const val = parseFloat(e.target.value) || 0; if (val !== v.amount) updateExpense(v.id, 'amount', val); }} />
                        ) : <span style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{fmtInt(v.amount)}</span>}
                      </td>
                      <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: 'var(--green)' }}>
                        {fmtInt(v.splitType === '100%' ? v.amount : v.amount / 2)}
                      </td>
                      {canEdit && <td>{!locked && <button className="btn btn-sm btn-danger" onClick={() => deleteExpense(v.id)}>✕</button>}</td>}
                    </tr>
                  ))}
                  {/* Add new row */}
                  {canEdit && !locked && (
                    <tr style={{ background: 'var(--surface2)' }}>
                      <td>
                        <input className="input input-sm" placeholder={he ? 'תיאור הוצאה...' : 'Expense description...'} value={mktNewDesc}
                          onChange={e => setMktNewDesc(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') addExpense(); }} />
                      </td>
                      <td>
                        <select className="input input-sm" value={mktNewSplit || '50/50'} onChange={e => setMktNewSplit(e.target.value)} style={{ width: 90 }}>
                          <option value="50/50">50/50</option>
                          <option value="100%">100% Marshall</option>
                        </select>
                      </td>
                      <td className="text-end">
                        <input className="input input-sm" type="number" step="0.01" placeholder="$" style={{ width: 120, textAlign: 'right' }} value={mktNewAmount}
                          onChange={e => setMktNewAmount(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') addExpense(); }} />
                      </td>
                      <td></td>
                      <td><button className="btn btn-sm btn-gold" onClick={addExpense}>+</button></td>
                    </tr>
                  )}
                  {/* Totals */}
                  <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                    <td colSpan={2}>{he ? 'סה"כ 50/50' : 'Total 50/50'}</td>
                    <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtInt(marketing.shared5050)}</td>
                    <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: 'var(--green)' }}>{fmtInt(marketing.shared5050 / 2)}</td>
                    {canEdit && <td></td>}
                  </tr>
                  {marketing.marshall100 > 0 && (
                    <tr style={{ fontWeight: 700 }}>
                      <td colSpan={2}>{he ? 'סה"כ 100% מרשל' : 'Total 100% Marshall'}</td>
                      <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtInt(marketing.marshall100)}</td>
                      <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: 'var(--green)' }}>{fmtInt(marketing.marshall100)}</td>
                      {canEdit && <td></td>}
                    </tr>
                  )}
                  <tr style={{ fontWeight: 800, borderTop: '2px solid var(--text)', background: 'var(--surface2)' }}>
                    <td colSpan={2}>{he ? 'סה"כ מרשל חייב' : 'Total Marshall Owes'}</td>
                    <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtInt(totalExpenses)}</td>
                    <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: 'var(--green)', fontSize: 16 }}>{fmtInt(marshallShare)}</td>
                    {canEdit && <td></td>}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Marshall MDF (Additional Budget) */}
            <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 8 }}>
              {he ? 'MDF מרשל — תקציב נוסף (100% מרשל)' : 'Marshall MDF — Additional Budget (100% Marshall)'}
            </h3>
            <div className="table-wrap" style={{ marginBottom: 20 }}>
              <table>
                <thead><tr>
                  <th>{he ? 'תיאור' : 'Description'}</th>
                  <th className="text-end">{he ? 'סכום' : 'Amount'}</th>
                  {canEdit && <th style={{ width: 50 }}></th>}
                </tr></thead>
                <tbody>
                  {(marketing.mdfRows || []).map(m => (
                    <tr key={m.id}>
                      <td>
                        {canEdit && !locked ? (
                          <input className="inline-edit" style={{ width: '100%' }} defaultValue={m.description}
                            onBlur={e => { if (e.target.value !== m.description) api.put(`/prices/marketing-mdf/${m.id}`, { description: e.target.value }).then(() => fetchData(true)); }} />
                        ) : m.description}
                      </td>
                      <td className="text-end">
                        {canEdit && !locked ? (
                          <input className="inline-edit" type="number" step="0.01" style={{ width: 120, textAlign: 'right' }} defaultValue={m.amount}
                            onBlur={e => { const val = parseFloat(e.target.value) || 0; if (val !== m.amount) api.put(`/prices/marketing-mdf/${m.id}`, { amount: val }).then(() => fetchData(true)); }} />
                        ) : <span style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{fmtInt(m.amount)}</span>}
                      </td>
                      {canEdit && <td>{!locked && <button className="btn btn-sm btn-danger" onClick={async () => { if (!(await confirm({ body: he ? 'למחוק?' : 'Delete?', danger: true }))) return; await api.delete(`/prices/marketing-mdf/${m.id}`); fetchData(true); }}>✕</button>}</td>}
                    </tr>
                  ))}
                  {canEdit && !locked && (
                    <tr style={{ background: 'var(--surface2)' }}>
                      <td>
                        <input className="input input-sm" placeholder={he ? 'תיאור MDF...' : 'MDF description...'} value={mdfNewDesc}
                          onChange={e => setMdfNewDesc(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && mdfNewDesc.trim()) { api.post('/prices/marketing-mdf', { quarter: mktQuarter, description: mdfNewDesc, amount: parseFloat(mdfNewAmount) || 0 }).then(() => { setMdfNewDesc(''); setMdfNewAmount(''); fetchData(true); }); }}} />
                      </td>
                      <td className="text-end">
                        <input className="input input-sm" type="number" step="0.01" placeholder="$" style={{ width: 120, textAlign: 'right' }} value={mdfNewAmount}
                          onChange={e => setMdfNewAmount(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && mdfNewDesc.trim()) { api.post('/prices/marketing-mdf', { quarter: mktQuarter, description: mdfNewDesc, amount: parseFloat(mdfNewAmount) || 0 }).then(() => { setMdfNewDesc(''); setMdfNewAmount(''); fetchData(true); }); }}} />
                      </td>
                      <td><button className="btn btn-sm btn-gold" onClick={() => { if (!mdfNewDesc.trim()) return; api.post('/prices/marketing-mdf', { quarter: mktQuarter, description: mdfNewDesc, amount: parseFloat(mdfNewAmount) || 0 }).then(() => { setMdfNewDesc(''); setMdfNewAmount(''); fetchData(true); }); }}>+</button></td>
                    </tr>
                  )}
                  {(marketing.mdfRows || []).length > 0 && (
                    <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                      <td>{he ? 'סה"כ MDF' : 'MDF Total'}</td>
                      <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: 'var(--green)' }}>{fmtInt(marketing.mdfTotal)}</td>
                      {canEdit && <td></td>}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Grand Total Formula */}
            <div className="formula">
              <div><span className="lbl">{he ? '50/50 × 50%' : '50/50 × 50%'}</span><span className="val">{fmtInt(marketing.shared5050 / 2)}</span></div>
              {marketing.marshall100 > 0 && <>
                <span className="op">+</span>
                <div><span className="lbl">{he ? '100% מרשל' : '100% Marshall'}</span><span className="val">{fmtInt(marketing.marshall100)}</span></div>
              </>}
              {(marketing.mdfTotal || 0) > 0 && <>
                <span className="op">+</span>
                <div><span className="lbl">MDF</span><span className="val">{fmtInt(marketing.mdfTotal)}</span></div>
              </>}
              <span className="op">=</span>
              <div><span className="lbl">{he ? 'סה"כ מרשל' : 'Total Marshall'}</span><span className="val" style={{ color: 'var(--green)' }}>{fmtInt(marshallShare + (marketing.mdfTotal || 0))}</span></div>
            </div>

            {/* Credit Received Section */}
            {(() => {
              const claimed = marshallShare + (marketing.mdfTotal || 0);
              const totalReceived = creditsReceived.reduce((s, c) => s + c.amount, 0);
              const outstanding = claimed - totalReceived;
              const addMktCredit = async () => {
                if (!newCredit.number.trim()) return;
                await api.post('/prices/credits-received', { type: 'marketing', quarter: mktQuarter, creditNumber: newCredit.number, amount: parseFloat(newCredit.amount) || 0 });
                setNewCredit({ number: '', amount: '' });
                fetchData(true);
              };
              const deleteCredit = async (id) => {
                if (!(await confirm({ body: he ? 'למחוק?' : 'Delete?', danger: true }))) return;
                await api.delete(`/prices/credits-received/${id}`);
                fetchData(true);
              };
              return (
                <div style={{ marginTop: 24 }}>
                  <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 8 }}>
                    {he ? 'זיכויים שהתקבלו' : 'Credits Received'}
                  </h3>
                  <div className="summary-cards" style={{ marginBottom: 16, gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <div className="summary-card info">
                      <div className="label">{he ? 'נדרש' : 'Claimed'}</div>
                      <div className="value">{fmtInt(claimed)}</div>
                    </div>
                    <div className="summary-card success">
                      <div className="label">{he ? 'התקבל' : 'Received'}</div>
                      <div className="value">{fmtInt(totalReceived)}</div>
                    </div>
                    <div className={`summary-card ${outstanding > 0 ? 'danger' : 'success'}`}>
                      <div className="label">{he ? 'יתרה' : 'Outstanding'}</div>
                      <div className="value">{fmtInt(outstanding)}</div>
                    </div>
                  </div>
                  <div className="table-wrap">
                    <table>
                      <thead><tr>
                        <th>{he ? 'מס׳ זיכוי' : 'Credit #'}</th>
                        <th className="text-end">{he ? 'סכום' : 'Amount'}</th>
                        {canEdit && <th style={{ width: 50 }}></th>}
                      </tr></thead>
                      <tbody>
                        {creditsReceived.map(c => (
                          <tr key={c.id}>
                            <td style={{ fontWeight: 600 }}>{c.creditNumber}</td>
                            <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{fmt(c.amount)}</td>
                            {canEdit && <td><button className="btn btn-sm btn-danger" onClick={() => deleteCredit(c.id)}>✕</button></td>}
                          </tr>
                        ))}
                        {canEdit && (
                          <tr style={{ background: 'var(--surface2)' }}>
                            <td>
                              <input className="input input-sm" placeholder={he ? 'מס׳ זיכוי...' : 'Credit note #...'} value={newCredit.number}
                                onChange={e => setNewCredit({ ...newCredit, number: e.target.value })}
                                onKeyDown={e => { if (e.key === 'Enter') addMktCredit(); }} />
                            </td>
                            <td className="text-end">
                              <input className="input input-sm" type="number" step="0.01" placeholder="$" style={{ width: 120, textAlign: 'right' }} value={newCredit.amount}
                                onChange={e => setNewCredit({ ...newCredit, amount: e.target.value })}
                                onKeyDown={e => { if (e.key === 'Enter') addMktCredit(); }} />
                            </td>
                            <td><button className="btn btn-sm btn-gold" onClick={addMktCredit}>+</button></td>
                          </tr>
                        )}
                        {creditsReceived.length > 0 && (
                          <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                            <td>{he ? 'סה"כ' : 'Total'}</td>
                            <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: 'var(--green)' }}>{fmt(totalReceived)}</td>
                            {canEdit && <td></td>}
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* ═══ QUARTERLY RMA TAB ═══ */}
      {tab === 'rma' && (() => {
        const quarters = data?.credits || [];
        const totalPurchases = quarters.reduce((s, r) => s + (parseFloat(r.siPurchases) || 0), 0);
        // RMA 1.75% applies to Q1 SI invoices only (business rule: from Q2+ already in product price).
        // Backend siDefective is already Q1-only restricted.
        const totalRma = quarters.reduce((s, r) => s + (parseFloat(r.siDefective) || 0), 0);

        const exportRmaPdf = (row) => {
          const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
          const fmtUSD = (n) => (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
          // XSS-safe: escape any DB-sourced or user-controlled strings (defense in depth). Fix 2026-05-27.
          const esc = (s) => String(s == null ? '' : s).replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]));
          const siPurchases = parseFloat(row.siPurchases) || 0;
          const rmaAmount = parseFloat(row.siDefective) || 0;
          if (rmaAmount <= 0) {
            alert(he ? `אין RMA לרבעון ${row.quarter} (1.75% חל רק על Q1)` : `No RMA for ${row.quarter} — 1.75% applies to Q1 only.`);
            return;
          }
          const w = window.open('', '_blank', 'width=900,height=600');
          w.document.write(`<!DOCTYPE html><html><head><title>RMA Credit Claim - ${esc(row.quarter)}</title>
<style>
  body { font-family: Arial, sans-serif; padding: 60px; color: #1a1d23; }
  h1 { font-size: 26px; border-bottom: 3px solid #1a1d23; padding-bottom: 12px; margin-bottom: 8px; }
  .meta { color: #666; font-size: 13px; margin-bottom: 40px; }
  table { width: 100%; border-collapse: collapse; margin-top: 20px; }
  th { text-align: left; padding: 12px 16px; background: #f4f5f7; border: 1px solid #dee2e6; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #666; }
  th.right { text-align: right; }
  td { padding: 14px 16px; border: 1px solid #dee2e6; font-size: 15px; }
  td.right { text-align: right; font-weight: 700; }
  td.amount { text-align: right; font-weight: 800; font-size: 17px; color: #1565c0; }
  .footer { margin-top: 60px; font-size: 11px; color: #999; border-top: 1px solid #eee; padding-top: 12px; }
  @media print { body { padding: 40px; } }
</style></head><body>
<h1>RMA Credit Claim — ${esc(row.quarter)}</h1>
<div class="meta">From: Shager Distribution &nbsp;|&nbsp; Date: ${esc(today)} &nbsp;|&nbsp; Subject: Defective Product Credit (1.75%)</div>

<table>
  <thead><tr>
    <th>Quarter</th>
    <th class="right">Total SI Purchases</th>
    <th class="right">RMA Rate</th>
    <th class="right">Credit Amount Due</th>
  </tr></thead>
  <tbody><tr>
    <td style="font-weight:700">${esc(row.quarter)}</td>
    <td class="right">${fmtUSD(siPurchases)}</td>
    <td class="right">1.75%</td>
    <td class="amount">${fmtUSD(rmaAmount)}</td>
  </tr></tbody>
</table>

<div class="footer">Generated by SCM Pro · ${esc(today)}</div>
</body></html>`);
          w.document.close();
          setTimeout(() => w.print(), 300);
        };

        return (
          <div>
            <div className="summary-cards" style={{ marginBottom: 24 }}>
              <div className="summary-card info">
                <div className="label">Total SI Purchases</div>
                <div className="value">{fmtInt(totalPurchases)}</div>
                <div className="sub">all quarters</div>
              </div>
              <div className="summary-card" style={{ background: '#e3f2fd' }}>
                <div className="label">RMA 1.75%</div>
                <div className="value" style={{ color: '#1565c0' }}>{fmtInt(totalRma)}</div>
                <div className="sub">total defective credit</div>
              </div>
            </div>

            {quarters.length === 0 ? <div className="empty-state">{t('common.noData')}</div> : (
              <div className="table-wrap">
                <table>
                  <thead><tr>
                    <th>Quarter</th>
                    <th className="text-end">SI Purchases</th>
                    <th className="text-end" style={{ color: '#1565c0' }}>RMA 1.75%</th>
                    <th style={{ width: 80, textAlign: 'center' }}>PDF</th>
                  </tr></thead>
                  <tbody>
                    {quarters.map((row, i) => {
                      const siPurchases = parseFloat(row.siPurchases) || 0;
                      const rma = parseFloat(row.siDefective) || 0;
                      const isQ1 = row.quarter && row.quarter.startsWith('Q1');
                      return (
                        <tr key={i}>
                          <td className="fw-bold">{row.quarter}</td>
                          <td className="text-end">{fmt(siPurchases)}</td>
                          <td className="text-end fw-bold" style={{ color: rma > 0 ? '#1565c0' : '#94a3b8' }}>
                            {rma > 0 ? fmt(rma) : (he ? '— (לא Q1)' : '— (not Q1)')}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {rma > 0 ? (
                              <button className="btn btn-sm btn-outline" onClick={() => exportRmaPdf(row)} title="Generate PDF">
                                📄
                              </button>
                            ) : (
                              <span style={{ color: '#cbd5e1' }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                      <td>{t('common.total')}</td>
                      <td className="text-end">{fmt(totalPurchases)}</td>
                      <td className="text-end" style={{ color: '#1565c0' }}>{fmt(totalRma)}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Credit Received Section */}
            {(() => {
              const he = i18n.language === 'he';
              const totalClaimed = totalRma;
              const totalReceived = creditsReceived.reduce((s, c) => s + c.amount, 0);
              const outstanding = totalClaimed - totalReceived;
              const addRmaCredit = async () => {
                if (!newCredit.number.trim()) return;
                await api.post('/prices/credits-received', { type: 'rma', quarter: newCredit.quarter || 'ALL', creditNumber: newCredit.number, amount: parseFloat(newCredit.amount) || 0 });
                setNewCredit({ number: '', amount: '' });
                fetchData(true);
              };
              const deleteCredit = async (id) => {
                if (!(await confirm({ body: he ? 'למחוק?' : 'Delete?', danger: true }))) return;
                await api.delete(`/prices/credits-received/${id}`);
                fetchData(true);
              };
              return (
                <div style={{ marginTop: 24 }}>
                  <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 8 }}>
                    {he ? 'זיכויים שהתקבלו — RMA' : 'Credits Received — RMA'}
                  </h3>
                  <div className="summary-cards" style={{ marginBottom: 16, gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <div className="summary-card info">
                      <div className="label">{he ? 'נדרש' : 'Claimed'}</div>
                      <div className="value">{fmtInt(totalClaimed)}</div>
                    </div>
                    <div className="summary-card success">
                      <div className="label">{he ? 'התקבל' : 'Received'}</div>
                      <div className="value">{fmtInt(totalReceived)}</div>
                    </div>
                    <div className={`summary-card ${outstanding > 0 ? 'danger' : 'success'}`}>
                      <div className="label">{he ? 'יתרה' : 'Outstanding'}</div>
                      <div className="value">{fmtInt(outstanding)}</div>
                    </div>
                  </div>
                  <div className="table-wrap">
                    <table>
                      <thead><tr>
                        <th>{he ? 'רבעון' : 'Quarter'}</th>
                        <th>{he ? 'מס׳ זיכוי' : 'Credit #'}</th>
                        <th className="text-end">{he ? 'סכום' : 'Amount'}</th>
                        {canEdit && <th style={{ width: 50 }}></th>}
                      </tr></thead>
                      <tbody>
                        {creditsReceived.map(c => (
                          <tr key={c.id}>
                            <td>{c.quarter}</td>
                            <td style={{ fontWeight: 600 }}>{c.creditNumber}</td>
                            <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{fmt(c.amount)}</td>
                            {canEdit && <td><button className="btn btn-sm btn-danger" onClick={() => deleteCredit(c.id)}>✕</button></td>}
                          </tr>
                        ))}
                        {canEdit && (
                          <tr style={{ background: 'var(--surface2)' }}>
                            <td>
                              <select className="input input-sm" value={newCredit.quarter || ''} onChange={e => setNewCredit({ ...newCredit, quarter: e.target.value })} style={{ width: 100 }}>
                                <option value="">—</option>
                                {quarters.map(q => <option key={q.quarter} value={q.quarter}>{q.quarter}</option>)}
                              </select>
                            </td>
                            <td>
                              <input className="input input-sm" placeholder={he ? 'מס׳ זיכוי...' : 'Credit note #...'} value={newCredit.number}
                                onChange={e => setNewCredit({ ...newCredit, number: e.target.value })}
                                onKeyDown={e => { if (e.key === 'Enter') addRmaCredit(); }} />
                            </td>
                            <td className="text-end">
                              <input className="input input-sm" type="number" step="0.01" placeholder="$" style={{ width: 120, textAlign: 'right' }} value={newCredit.amount}
                                onChange={e => setNewCredit({ ...newCredit, amount: e.target.value })}
                                onKeyDown={e => { if (e.key === 'Enter') addRmaCredit(); }} />
                            </td>
                            <td><button className="btn btn-sm btn-gold" onClick={addRmaCredit}>+</button></td>
                          </tr>
                        )}
                        {creditsReceived.length > 0 && (
                          <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                            <td colSpan={2}>{he ? 'סה"כ' : 'Total'}</td>
                            <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: 'var(--green)' }}>{fmt(totalReceived)}</td>
                            {canEdit && <td></td>}
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}
    </div>
  );
}
